// Voice mode: the voice control, the voice bar, the pending turn, the first-use
// disclosure, voice-mode settings and the company switches (BUILD 2 C2-13).
//
// Namespace `voice`; owner WP2-8 (BUILD 2), in all 24 app languages. Only the
// owner adds keys or translations. Placeholders are `{name}`, filled with
// `formatCopy`. See ./index.ts.
// Language is automatic and never shown: there is no language or voice key.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  voiceMode: 'Voice mode',
  enterVoice: 'Talk with Poneva',
  exitVoice: 'Exit voice mode',
  listening: 'Listening…',
  hearing: 'Hearing you…',
  cancelSpeaking: 'Cancel',
  finishSpeaking: 'Done speaking',
  sendingIn: 'Sending in {seconds} s',
  sendNow: 'Send now',
  edit: 'Edit',
  discard: 'Discard',
  alwaysAsk: 'Always ask before sending',
  undoWindow: 'Undo window',
  settingsPageTitle: 'Voice',
  waitBeforeSending: 'Wait before sending',
  paused: 'Voice mode is paused.',
  resumeVoice: 'Resume voice',
  voiceUnavailable: 'Voice mode is not available right now.',
  errorNotConfigured: 'Voice mode is not set up on this server.',
  errorPolicyOff: 'Your company has turned voice mode off.',
  errorNoEngine: 'No speech service for voice mode is available right now.',
  errorTtsUnavailable: 'Read-aloud is not available right now.',
  errorPaymentRequired:
    'Your company’s usage credit is used up. An administrator can add a card, then try again.',
  micHeldByVoice: 'The microphone is in use by voice mode.',
  voiceRefusedMeeting: 'Voice mode is off while a meeting is recording.',
  typeInstead: 'Type instead',
  disclosureTitle: 'Talk with Poneva',
  disclosureBody:
    'While voice mode is on, what you say is sent to our speech-to-text providers ({processors}) and becomes your message.',
  disclosureReadAloud: 'Replies read aloud are voiced by {processors}.',
  disclosureApprovals:
    'Your message is sent after a short pause that you can undo. Approvals and changes to your data always wait for you to confirm them.',
  disclosureExit: 'Tap Exit, or ask Poneva to leave voice mode, at any time.',
  turnOn: 'Turn on',
  notNow: 'Not now',
  degradedTapToSend: 'Switched to a backup transcription service. Tap to send.',
  idleExited: 'Voice mode ended after a quiet while.',
  settingsTitle: 'Voice mode',
  handsFreeLabel: 'Hands-free',
  readAloudInVoice: 'Read replies aloud',
  speakWith: 'Speak replies with',
  speakWithPoneva: 'Poneva’s voice',
  screenReader: 'My screen reader',
  rate: 'Speaking rate',
  spokenApprovalsLabel: 'Confirm approvals by voice',
  companyVoiceMode: 'Voice mode',
  companyReadAloud: 'Allow read-aloud',
  companySpokenApprovals: 'Allow approvals by voice',
  starting: 'Starting voice mode…',
  finishing: 'Finishing…',
  answering: 'Poneva is answering…',
  speakingInterrupt: 'Reading the reply aloud. Press to interrupt.',
  stopReply: 'Stop reading aloud',
  reconnecting: 'Reconnecting…',
  degraded: 'Switched to a backup transcription service.',
  micOn: 'Microphone on',
  pendingLabel: 'Your message, not sent yet',
  pendingAnnouncement: 'Your message is ready to send. Enter sends it now, Escape discards it.',
  tapToSend: 'Tap Send now to send it.',
  answerCardFirst: 'Answer the card above first, or tap Send now.',
  noSpeechHeard: 'No speech was heard.',
  replacedElsewhere: 'Voice mode started in another tab or app.',
  pausedHidden: 'Voice mode paused while the page was hidden.',
  draftKept: 'Your unsent message was kept as a draft.',
  errorPermission: 'Allow the microphone in your browser or device settings, then try again.',
  errorNoMicrophone: 'No microphone was found.',
  errorMicrophoneBusy: 'The microphone is in use by another app.',
  errorMicrophoneLost: 'The microphone was disconnected.',
  errorInsecure: 'Voice mode needs a secure (https) connection.',
  errorUnsupported: 'This browser cannot record audio for voice mode.',
  errorRateLimited: 'Voice mode was started too often. Try again in {seconds} s.',
  errorBudget: 'Today’s voice mode allowance is used up.',
  errorOffline: 'You are offline. Voice mode is paused.',
  errorOriginNotAllowed:
    'This page origin is not allowed for voice mode. Open Chat from the approved address.',
  errorProviderUnavailable: 'The speech service is temporarily unavailable. Try again shortly.',
  errorSignIn: 'Sign in to use voice mode.',
  errorCompanyRequired: 'Voice mode is available in a company workspace.',
  errorGeneric: 'Voice mode stopped because of a problem. Try again.',
  controlStop: 'Stopped reading aloud.',
  controlRepeat: 'Reading the reply again.',
  controlExit: 'Left voice mode.',
  controlRate: 'Speaking rate set to {rate}×.',
  settingsDescription: 'Talk with Poneva instead of typing, and hear its replies.',
  enabledLabel: 'Use voice mode',
  enabledHint: 'When this is off, Poneva explains voice mode before it starts.',
  handsFreeHint: 'Poneva listens and sends when you pause.',
  undoEndpoint: 'After you stop talking (hands-free)',
  undoSeconds: '{seconds} s',
  rateValue: '{rate}×',
  spokenApprovalsHint:
    'Answer a confirmation card by saying yes or no. Actions in connected services always need a tap.',
  saved: 'Saved',
  saveFailed: 'Your change could not be saved. Try again.',
  policyTitle: 'Voice and read-aloud',
  policyDescription: 'These switches apply to everyone in your company.',
  companyVoiceModeHint: 'Members can use voice mode when spoken replies are available.',
  companyReadAloudHint: 'Poneva can read its replies aloud.',
  companySpokenApprovalsHint:
    'Members can confirm actions by voice. Actions in connected services always need a tap.',
};

export type VoiceCopyKey = keyof typeof en;

const voiceCopy: NamespaceCatalog<VoiceCopyKey> = {
  bg: {
    voiceMode: 'Гласов режим',
    enterVoice: 'Говорете с Poneva',
    exitVoice: 'Изход от гласовия режим',
    listening: 'Слуша…',
    hearing: 'Чувам ви…',
    cancelSpeaking: 'Отказ',
    finishSpeaking: 'Готово',
    sendingIn: 'Изпращане след {seconds} с',
    sendNow: 'Изпрати сега',
    edit: 'Редактиране',
    discard: 'Отхвърляне',
    alwaysAsk: 'Винаги питай преди изпращане',
    undoWindow: 'Прозорец за отмяна',
    settingsPageTitle: 'Глас',
    waitBeforeSending: 'Изчакване преди изпращане',
    paused: 'Гласовият режим е на пауза.',
    resumeVoice: 'Продължи с глас',
    voiceUnavailable: 'Гласовият режим в момента не е наличен.',
    errorNotConfigured: 'Гласовият режим не е настроен на този сървър.',
    errorPolicyOff: 'Вашата фирма е изключила гласовия режим.',
    errorNoEngine: 'В момента няма налична говорна услуга за гласовия режим.',
    errorTtsUnavailable: 'Четенето на глас в момента не е налично.',
    errorPaymentRequired:
      'Кредитът за използване на вашата фирма е изчерпан. Администратор може да добави карта; след това опитайте отново.',
    micHeldByVoice: 'Микрофонът се използва от гласовия режим.',
    voiceRefusedMeeting: 'Гласовият режим е изключен, докато се записва среща.',
    typeInstead: 'Пишете вместо това',
    disclosureTitle: 'Говорете с Poneva',
    disclosureBody:
      'Докато гласовият режим е включен, казаното от вас се изпраща към нашите доставчици за преобразуване на реч в текст ({processors}) и става ваше съобщение.',
    disclosureReadAloud: 'Отговорите, прочетени на глас, се озвучават от {processors}.',
    disclosureApprovals:
      'Съобщението ви се изпраща след кратка пауза, която можете да отмените. Одобренията и промените в данните ви винаги чакат вашето потвърждение.',
    disclosureExit:
      'По всяко време докоснете „Изход“ или помолете Poneva да излезе от гласовия режим.',
    turnOn: 'Включване',
    notNow: 'Не сега',
    degradedTapToSend:
      'Превключено към резервна услуга за транскрипция. Докоснете, за да изпратите.',
    idleExited: 'Гласовият режим приключи след известно време тишина.',
    settingsTitle: 'Гласов режим',
    handsFreeLabel: 'Без ръце',
    readAloudInVoice: 'Четене на отговорите на глас',
    speakWith: 'Четене на отговорите с',
    speakWithPoneva: 'Гласа на Poneva',
    screenReader: 'Моя екранен четец',
    rate: 'Скорост на говора',
    spokenApprovalsLabel: 'Потвърждаване на одобрения с глас',
    companyVoiceMode: 'Гласов режим',
    companyReadAloud: 'Разрешаване на четенето на глас',
    companySpokenApprovals: 'Разрешаване на одобрения с глас',
    starting: 'Гласовият режим се стартира…',
    finishing: 'Завършване…',
    answering: 'Poneva отговаря…',
    speakingInterrupt: 'Отговорът се чете. Натиснете, за да прекъснете.',
    stopReply: 'Спиране на четенето на глас',
    reconnecting: 'Повторно свързване…',
    degraded: 'Превключено към резервна услуга за транскрипция.',
    micOn: 'Микрофонът е включен',
    pendingLabel: 'Вашето съобщение, още неизпратено',
    pendingAnnouncement:
      'Съобщението ви е готово за изпращане. Enter го изпраща сега, Escape го отхвърля.',
    tapToSend: 'Докоснете „Изпрати сега“, за да го изпратите.',
    answerCardFirst: 'Първо отговорете на картата по-горе или докоснете „Изпрати сега“.',
    noSpeechHeard: 'Не се чу говор.',
    replacedElsewhere: 'Гласовият режим беше стартиран в друг раздел или приложение.',
    pausedHidden: 'Гласовият режим беше поставен на пауза, когато страницата беше скрита.',
    draftKept: 'Неизпратеното ви съобщение е запазено като чернова.',
    errorPermission:
      'Разрешете микрофона в настройките на браузъра или устройството и опитайте отново.',
    errorNoMicrophone: 'Не е намерен микрофон.',
    errorMicrophoneBusy: 'Микрофонът се използва от друго приложение.',
    errorMicrophoneLost: 'Микрофонът беше изключен.',
    errorInsecure: 'Гласовият режим изисква защитена връзка (https).',
    errorUnsupported: 'Този браузър не може да записва звук за гласовия режим.',
    errorRateLimited:
      'Гласовият режим беше стартиран твърде често. Опитайте отново след {seconds} с.',
    errorBudget: 'Днешната квота за гласовия режим е изчерпана.',
    errorOffline: 'Нямате връзка. Гласовият режим е на пауза.',
    errorOriginNotAllowed:
      'Произходът на тази страница не е разрешен за гласов режим. Отворете Chat от одобрения адрес.',
    errorProviderUnavailable: 'Услугата за говор временно не е налична. Опитайте отново скоро.',
    errorSignIn: 'Влезте, за да използвате гласовия режим.',
    errorCompanyRequired: 'Гласовият режим е наличен във фирмено работно пространство.',
    errorGeneric: 'Гласовият режим спря поради проблем. Опитайте отново.',
    controlStop: 'Четенето на глас беше спряно.',
    controlRepeat: 'Отговорът се чете отново.',
    controlExit: 'Излязохте от гласовия режим.',
    controlRate: 'Скоростта на говора е зададена на {rate}×.',
    settingsDescription: 'Говорете с Poneva, вместо да пишете, и слушайте отговорите.',
    enabledLabel: 'Използване на гласовия режим',
    enabledHint: 'Когато това е изключено, Poneva обяснява гласовия режим, преди да започне.',
    handsFreeHint: 'Poneva слуша и изпраща, когато направите пауза.',
    undoEndpoint: 'След като спрете да говорите (без ръце)',
    undoSeconds: '{seconds} с',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Отговорете на карта за потвърждение, като кажете „да“ или „не“. Действията в свързани услуги винаги изискват докосване.',
    saved: 'Запазено',
    saveFailed: 'Промяната не можа да бъде запазена. Опитайте отново.',
    policyTitle: 'Глас и четене на глас',
    policyDescription: 'Тези настройки важат за всички във вашата фирма.',
    companyVoiceModeHint:
      'Членовете могат да използват гласов режим, когато са налични гласови отговори.',
    companyReadAloudHint: 'Poneva може да чете отговорите си на глас.',
    companySpokenApprovalsHint:
      'Членовете могат да потвърждават действия с глас. Действията в свързани услуги винаги изискват докосване.',
  },
  hr: {
    voiceMode: 'Glasovni način',
    enterVoice: 'Razgovaraj s Ponevom',
    exitVoice: 'Izađi iz glasovnog načina',
    listening: 'Slušam…',
    hearing: 'Čujem vas…',
    cancelSpeaking: 'Odustani',
    finishSpeaking: 'Gotovo',
    sendingIn: 'Slanje za {seconds} s',
    sendNow: 'Pošalji sada',
    edit: 'Uredi',
    discard: 'Odbaci',
    alwaysAsk: 'Uvijek pitaj prije slanja',
    undoWindow: 'Prozor za poništavanje',
    settingsPageTitle: 'Glas',
    waitBeforeSending: 'Čekanje prije slanja',
    paused: 'Glasovni način je pauziran.',
    resumeVoice: 'Nastavi glasom',
    voiceUnavailable: 'Glasovni način trenutačno nije dostupan.',
    errorNotConfigured: 'Glasovni način nije postavljen na ovom poslužitelju.',
    errorPolicyOff: 'Vaša je tvrtka isključila glasovni način.',
    errorNoEngine: 'Za glasovni način trenutačno nije dostupna nijedna govorna usluga.',
    errorTtsUnavailable: 'Čitanje naglas trenutačno nije dostupno.',
    errorPaymentRequired:
      'Kredit za korištenje vaše tvrtke je potrošen. Administrator može dodati karticu; zatim pokušajte ponovno.',
    micHeldByVoice: 'Mikrofon koristi glasovni način.',
    voiceRefusedMeeting: 'Glasovni način je isključen dok se snima sastanak.',
    typeInstead: 'Radije tipkaj',
    disclosureTitle: 'Razgovaraj s Ponevom',
    disclosureBody:
      'Dok je glasovni način uključen, ono što kažete šalje se našim pružateljima pretvorbe govora u tekst ({processors}) i postaje vaša poruka.',
    disclosureReadAloud: 'Odgovore koji se čitaju naglas izgovara {processors}.',
    disclosureApprovals:
      'Poruka se šalje nakon kratke stanke koju možete poništiti. Odobrenja i promjene vaših podataka uvijek čekaju vašu potvrdu.',
    disclosureExit:
      'U bilo kojem trenutku dodirnite Izađi ili zamolite Ponevu da izađe iz glasovnog načina.',
    turnOn: 'Uključi',
    notNow: 'Ne sada',
    degradedTapToSend: 'Prebačeno na pričuvnu uslugu transkripcije. Dodirnite za slanje.',
    idleExited: 'Glasovni način završio je nakon duže tišine.',
    settingsTitle: 'Glasovni način',
    handsFreeLabel: 'Bez ruku',
    readAloudInVoice: 'Čitaj odgovore naglas',
    speakWith: 'Čitaj odgovore pomoću',
    speakWithPoneva: 'Ponevina glasa',
    screenReader: 'Mojeg čitača zaslona',
    rate: 'Brzina govora',
    spokenApprovalsLabel: 'Potvrđuj odobrenja glasom',
    companyVoiceMode: 'Glasovni način',
    companyReadAloud: 'Dopusti čitanje naglas',
    companySpokenApprovals: 'Dopusti odobravanje glasom',
    starting: 'Pokretanje glasovnog načina…',
    finishing: 'Dovršavanje…',
    answering: 'Poneva odgovara…',
    speakingInterrupt: 'Čitanje odgovora. Pritisnite za prekid.',
    stopReply: 'Zaustavi čitanje naglas',
    reconnecting: 'Ponovno povezivanje…',
    degraded: 'Prebačeno na pričuvnu uslugu transkripcije.',
    micOn: 'Mikrofon je uključen',
    pendingLabel: 'Vaša poruka, još nije poslana',
    pendingAnnouncement: 'Poruka je spremna za slanje. Enter je šalje sada, Escape je odbacuje.',
    tapToSend: 'Dodirnite Pošalji sada da biste je poslali.',
    answerCardFirst: 'Najprije odgovorite na karticu iznad ili dodirnite Pošalji sada.',
    noSpeechHeard: 'Govor se nije čuo.',
    replacedElsewhere: 'Glasovni način pokrenut je u drugoj kartici ili aplikaciji.',
    pausedHidden: 'Glasovni način pauziran je kad je stranica bila skrivena.',
    draftKept: 'Neposlana poruka spremljena je kao skica.',
    errorPermission: 'Dopustite mikrofon u postavkama preglednika ili uređaja i pokušajte ponovno.',
    errorNoMicrophone: 'Mikrofon nije pronađen.',
    errorMicrophoneBusy: 'Mikrofon koristi druga aplikacija.',
    errorMicrophoneLost: 'Mikrofon je isključen.',
    errorInsecure: 'Glasovni način zahtijeva sigurnu vezu (https).',
    errorUnsupported: 'Ovaj preglednik ne može snimati zvuk za glasovni način.',
    errorRateLimited: 'Glasovni način pokrenut je prečesto. Pokušajte ponovno za {seconds} s.',
    errorBudget: 'Današnja kvota glasovnog načina je potrošena.',
    errorOffline: 'Niste povezani. Glasovni način je pauziran.',
    errorOriginNotAllowed:
      'Izvor ove stranice nije dopušten za glasovni način. Otvorite Chat s odobrene adrese.',
    errorProviderUnavailable: 'Govorna usluga privremeno nije dostupna. Pokušajte ponovno uskoro.',
    errorSignIn: 'Prijavite se kako biste koristili glasovni način.',
    errorCompanyRequired: 'Glasovni način dostupan je u radnom prostoru tvrtke.',
    errorGeneric: 'Glasovni način zaustavio se zbog problema. Pokušajte ponovno.',
    controlStop: 'Čitanje naglas zaustavljeno.',
    controlRepeat: 'Odgovor se ponovno čita.',
    controlExit: 'Izašli ste iz glasovnog načina.',
    controlRate: 'Brzina govora postavljena je na {rate}×.',
    settingsDescription: 'Razgovarajte s Ponevom umjesto tipkanja i slušajte odgovore.',
    enabledLabel: 'Koristi glasovni način',
    enabledHint: 'Kad je ovo isključeno, Poneva objašnjava glasovni način prije pokretanja.',
    handsFreeHint: 'Poneva sluša i šalje kad napravite stanku.',
    undoEndpoint: 'Nakon što prestanete govoriti (bez ruku)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Odgovorite na karticu potvrde s da ili ne. Radnje u povezanim uslugama uvijek zahtijevaju dodir.',
    saved: 'Spremljeno',
    saveFailed: 'Promjenu nije bilo moguće spremiti. Pokušajte ponovno.',
    policyTitle: 'Glas i čitanje naglas',
    policyDescription: 'Ove postavke vrijede za sve u vašoj tvrtki.',
    companyVoiceModeHint:
      'Članovi mogu koristiti glasovni način kada su dostupni govorni odgovori.',
    companyReadAloudHint: 'Poneva može čitati svoje odgovore naglas.',
    companySpokenApprovalsHint:
      'Članovi mogu potvrđivati radnje glasom. Radnje u povezanim uslugama uvijek zahtijevaju dodir.',
  },
  cs: {
    voiceMode: 'Hlasový režim',
    enterVoice: 'Mluvit s Ponevou',
    exitVoice: 'Ukončit hlasový režim',
    listening: 'Poslouchám…',
    hearing: 'Slyším vás…',
    cancelSpeaking: 'Zrušit',
    finishSpeaking: 'Domluveno',
    sendingIn: 'Odeslání za {seconds} s',
    sendNow: 'Odeslat hned',
    edit: 'Upravit',
    discard: 'Zahodit',
    alwaysAsk: 'Před odesláním se vždy zeptat',
    undoWindow: 'Okno pro vrácení',
    settingsPageTitle: 'Hlas',
    waitBeforeSending: 'Čekání před odesláním',
    paused: 'Hlasový režim je pozastavený.',
    resumeVoice: 'Pokračovat hlasem',
    voiceUnavailable: 'Hlasový režim teď není k dispozici.',
    errorNotConfigured: 'Hlasový režim není na tomto serveru nastavený.',
    errorPolicyOff: 'Vaše firma hlasový režim vypnula.',
    errorNoEngine: 'Pro hlasový režim teď není k dispozici žádná řečová služba.',
    errorTtsUnavailable: 'Čtení nahlas teď není k dispozici.',
    errorPaymentRequired:
      'Kredit za využití vaší firmy je vyčerpaný. Správce může přidat kartu; pak to zkuste znovu.',
    micHeldByVoice: 'Mikrofon používá hlasový režim.',
    voiceRefusedMeeting: 'Hlasový režim je vypnutý, dokud se nahrává schůzka.',
    typeInstead: 'Raději psát',
    disclosureTitle: 'Mluvit s Ponevou',
    disclosureBody:
      'Když je hlasový režim zapnutý, to, co řeknete, se posílá našim poskytovatelům převodu řeči na text ({processors}) a stane se z toho vaše zpráva.',
    disclosureReadAloud: 'Odpovědi čtené nahlas namlouvá {processors}.',
    disclosureApprovals:
      'Zpráva se odešle po krátké pauze, kterou můžete vrátit. Schválení a změny vašich dat vždy čekají na vaše potvrzení.',
    disclosureExit: 'Kdykoli klepněte na Ukončit nebo požádejte Ponevu, ať hlasový režim ukončí.',
    turnOn: 'Zapnout',
    notNow: 'Teď ne',
    degradedTapToSend: 'Přepnuto na záložní službu přepisu. Klepnutím odešlete.',
    idleExited: 'Hlasový režim skončil po chvíli ticha.',
    settingsTitle: 'Hlasový režim',
    handsFreeLabel: 'Hands-free',
    readAloudInVoice: 'Číst odpovědi nahlas',
    speakWith: 'Číst odpovědi pomocí',
    speakWithPoneva: 'Hlasu Ponevy',
    screenReader: 'Mé čtečky obrazovky',
    rate: 'Rychlost řeči',
    spokenApprovalsLabel: 'Potvrzovat schválení hlasem',
    companyVoiceMode: 'Hlasový režim',
    companyReadAloud: 'Povolit čtení nahlas',
    companySpokenApprovals: 'Povolit schvalování hlasem',
    starting: 'Spouštění hlasového režimu…',
    finishing: 'Dokončování…',
    answering: 'Poneva odpovídá…',
    speakingInterrupt: 'Čtení odpovědi. Stisknutím přerušíte.',
    stopReply: 'Zastavit čtení nahlas',
    reconnecting: 'Obnovování připojení…',
    degraded: 'Přepnuto na záložní službu přepisu.',
    micOn: 'Mikrofon zapnutý',
    pendingLabel: 'Vaše zpráva, zatím neodeslaná',
    pendingAnnouncement: 'Zpráva je připravená k odeslání. Enter ji odešle hned, Escape ji zahodí.',
    tapToSend: 'Klepnutím na Odeslat hned ji odešlete.',
    answerCardFirst: 'Nejdřív odpovězte na kartu výše, nebo klepněte na Odeslat hned.',
    noSpeechHeard: 'Nebyla slyšet žádná řeč.',
    replacedElsewhere: 'Hlasový režim se spustil v jiné kartě nebo aplikaci.',
    pausedHidden: 'Hlasový režim se pozastavil, když byla stránka skrytá.',
    draftKept: 'Neodeslaná zpráva byla uložena jako koncept.',
    errorPermission: 'Povolte mikrofon v nastavení prohlížeče nebo zařízení a zkuste to znovu.',
    errorNoMicrophone: 'Nebyl nalezen žádný mikrofon.',
    errorMicrophoneBusy: 'Mikrofon používá jiná aplikace.',
    errorMicrophoneLost: 'Mikrofon byl odpojen.',
    errorInsecure: 'Hlasový režim vyžaduje zabezpečené připojení (https).',
    errorUnsupported: 'Tento prohlížeč neumí nahrávat zvuk pro hlasový režim.',
    errorRateLimited: 'Hlasový režim byl spuštěn příliš často. Zkuste to znovu za {seconds} s.',
    errorBudget: 'Dnešní kvóta hlasového režimu je vyčerpaná.',
    errorOffline: 'Jste offline. Hlasový režim je pozastavený.',
    errorOriginNotAllowed:
      'Původ této stránky není pro hlasový režim povolen. Otevřete Chat ze schválené adresy.',
    errorProviderUnavailable: 'Řečová služba je dočasně nedostupná. Zkuste to brzy znovu.',
    errorSignIn: 'Pro použití hlasového režimu se přihlaste.',
    errorCompanyRequired: 'Hlasový režim je k dispozici ve firemním pracovním prostoru.',
    errorGeneric: 'Hlasový režim se kvůli problému zastavil. Zkuste to znovu.',
    controlStop: 'Čtení nahlas zastaveno.',
    controlRepeat: 'Odpověď se čte znovu.',
    controlExit: 'Hlasový režim ukončen.',
    controlRate: 'Rychlost řeči nastavena na {rate}×.',
    settingsDescription: 'Mluvte s Ponevou místo psaní a poslouchejte její odpovědi.',
    enabledLabel: 'Používat hlasový režim',
    enabledHint: 'Když je to vypnuté, Poneva hlasový režim před spuštěním vysvětlí.',
    handsFreeHint: 'Poneva poslouchá a odešle, když se odmlčíte.',
    undoEndpoint: 'Po skončení mluvení (hands-free)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Odpovězte na potvrzovací kartu slovem ano nebo ne. Akce v připojených službách vždy vyžadují klepnutí.',
    saved: 'Uloženo',
    saveFailed: 'Změnu se nepodařilo uložit. Zkuste to znovu.',
    policyTitle: 'Hlas a čtení nahlas',
    policyDescription: 'Tato nastavení platí pro všechny ve vaší firmě.',
    companyVoiceModeHint:
      'Členové mohou používat hlasový režim, když jsou k dispozici hlasové odpovědi.',
    companyReadAloudHint: 'Poneva může své odpovědi číst nahlas.',
    companySpokenApprovalsHint:
      'Členové mohou potvrzovat akce hlasem. Akce v připojených službách vždy vyžadují klepnutí.',
  },
  da: {
    voiceMode: 'Stemmetilstand',
    enterVoice: 'Tal med Poneva',
    exitVoice: 'Afslut stemmetilstand',
    listening: 'Lytter…',
    hearing: 'Hører dig…',
    cancelSpeaking: 'Annullér',
    finishSpeaking: 'Færdig',
    sendingIn: 'Sendes om {seconds} s',
    sendNow: 'Send nu',
    edit: 'Redigér',
    discard: 'Kassér',
    alwaysAsk: 'Spørg altid, før der sendes',
    undoWindow: 'Fortrydelsesvindue',
    settingsPageTitle: 'Stemme',
    waitBeforeSending: 'Vent før afsendelse',
    paused: 'Stemmetilstand er sat på pause.',
    resumeVoice: 'Fortsæt med stemme',
    voiceUnavailable: 'Stemmetilstand er ikke tilgængelig lige nu.',
    errorNotConfigured: 'Stemmetilstand er ikke sat op på denne server.',
    errorPolicyOff: 'Din virksomhed har slået stemmetilstand fra.',
    errorNoEngine: 'Ingen taletjeneste til stemmetilstand er tilgængelig lige nu.',
    errorTtsUnavailable: 'Oplæsning er ikke tilgængelig lige nu.',
    errorPaymentRequired:
      'Din virksomheds forbrugskredit er brugt op. En administrator kan tilføje et kort; prøv derefter igen.',
    micHeldByVoice: 'Mikrofonen bruges af stemmetilstand.',
    voiceRefusedMeeting: 'Stemmetilstand er slået fra, mens et møde optages.',
    typeInstead: 'Skriv i stedet',
    disclosureTitle: 'Tal med Poneva',
    disclosureBody:
      'Når stemmetilstand er slået til, sendes det, du siger, til vores tale-til-tekst-udbydere ({processors}) og bliver til din besked.',
    disclosureReadAloud: 'Svar, der læses højt, får stemme fra {processors}.',
    disclosureApprovals:
      'Din besked sendes efter en kort pause, som du kan fortryde. Godkendelser og ændringer af dine data venter altid på, at du bekræfter dem.',
    disclosureExit: 'Tryk på Afslut, eller bed Poneva om at forlade stemmetilstand, når som helst.',
    turnOn: 'Slå til',
    notNow: 'Ikke nu',
    degradedTapToSend: 'Skiftede til en reservetjeneste til transskription. Tryk for at sende.',
    idleExited: 'Stemmetilstand sluttede efter et stykke tid uden tale.',
    settingsTitle: 'Stemmetilstand',
    handsFreeLabel: 'Håndfri',
    readAloudInVoice: 'Læs svar højt',
    speakWith: 'Læs svar højt med',
    speakWithPoneva: 'Ponevas stemme',
    screenReader: 'Min skærmlæser',
    rate: 'Talehastighed',
    spokenApprovalsLabel: 'Bekræft godkendelser med stemmen',
    companyVoiceMode: 'Stemmetilstand',
    companyReadAloud: 'Tillad oplæsning',
    companySpokenApprovals: 'Tillad godkendelser med stemmen',
    starting: 'Starter stemmetilstand…',
    finishing: 'Gør færdig…',
    answering: 'Poneva svarer…',
    speakingInterrupt: 'Læser svaret højt. Tryk for at afbryde.',
    stopReply: 'Stop oplæsningen',
    reconnecting: 'Genopretter forbindelsen…',
    degraded: 'Skiftede til en reservetjeneste til transskription.',
    micOn: 'Mikrofonen er tændt',
    pendingLabel: 'Din besked, ikke sendt endnu',
    pendingAnnouncement:
      'Din besked er klar til at blive sendt. Enter sender den nu, Escape kasserer den.',
    tapToSend: 'Tryk på Send nu for at sende den.',
    answerCardFirst: 'Svar først på kortet ovenfor, eller tryk på Send nu.',
    noSpeechHeard: 'Der blev ikke hørt nogen tale.',
    replacedElsewhere: 'Stemmetilstand blev startet i en anden fane eller app.',
    pausedHidden: 'Stemmetilstand blev sat på pause, da siden blev skjult.',
    draftKept: 'Din usendte besked blev gemt som kladde.',
    errorPermission: 'Tillad mikrofonen i browserens eller enhedens indstillinger, og prøv igen.',
    errorNoMicrophone: 'Der blev ikke fundet nogen mikrofon.',
    errorMicrophoneBusy: 'Mikrofonen bruges af en anden app.',
    errorMicrophoneLost: 'Mikrofonen blev afbrudt.',
    errorInsecure: 'Stemmetilstand kræver en sikker forbindelse (https).',
    errorUnsupported: 'Denne browser kan ikke optage lyd til stemmetilstand.',
    errorRateLimited: 'Stemmetilstand blev startet for ofte. Prøv igen om {seconds} s.',
    errorBudget: 'Dagens kvote for stemmetilstand er brugt op.',
    errorOffline: 'Du er offline. Stemmetilstand er sat på pause.',
    errorOriginNotAllowed:
      'Denne sideoprindelse er ikke tilladt til stemmetilstand. Åbn Chat fra den godkendte adresse.',
    errorProviderUnavailable: 'Talefunktion er midlertidigt utilgængelig. Prøv igen om lidt.',
    errorSignIn: 'Log ind for at bruge stemmetilstand.',
    errorCompanyRequired: 'Stemmetilstand er tilgængelig i et virksomhedsarbejdsområde.',
    errorGeneric: 'Stemmetilstand stoppede på grund af et problem. Prøv igen.',
    controlStop: 'Stoppede oplæsningen.',
    controlRepeat: 'Læser svaret højt igen.',
    controlExit: 'Forlod stemmetilstand.',
    controlRate: 'Talehastigheden er sat til {rate}×.',
    settingsDescription: 'Tal med Poneva i stedet for at skrive, og hør svarene.',
    enabledLabel: 'Brug stemmetilstand',
    enabledHint: 'Når dette er slået fra, forklarer Poneva stemmetilstand, før den starter.',
    handsFreeHint: 'Poneva lytter og sender, når du holder en pause.',
    undoEndpoint: 'Efter du holder op med at tale (håndfri)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Svar på et bekræftelseskort ved at sige ja eller nej. Handlinger i forbundne tjenester kræver altid et tryk.',
    saved: 'Gemt',
    saveFailed: 'Ændringen kunne ikke gemmes. Prøv igen.',
    policyTitle: 'Stemme og oplæsning',
    policyDescription: 'Disse indstillinger gælder for alle i din virksomhed.',
    companyVoiceModeHint: 'Medlemmer kan bruge stemmetilstand, når talte svar er tilgængelige.',
    companyReadAloudHint: 'Poneva kan læse sine svar højt.',
    companySpokenApprovalsHint:
      'Medlemmer kan bekræfte handlinger med stemmen. Handlinger i forbundne tjenester kræver altid et tryk.',
  },
  nl: {
    voiceMode: 'Spraakmodus',
    enterVoice: 'Praat met Poneva',
    exitVoice: 'Spraakmodus verlaten',
    listening: 'Luistert…',
    hearing: 'Hoort je…',
    cancelSpeaking: 'Annuleren',
    finishSpeaking: 'Klaar met praten',
    sendingIn: 'Wordt verzonden over {seconds} s',
    sendNow: 'Nu verzenden',
    edit: 'Bewerken',
    discard: 'Weggooien',
    alwaysAsk: 'Altijd vragen vóór het verzenden',
    undoWindow: 'Periode voor ongedaan maken',
    settingsPageTitle: 'Spraak',
    waitBeforeSending: 'Wachten vóór verzenden',
    paused: 'Spraakmodus is gepauzeerd.',
    resumeVoice: 'Spraak hervatten',
    voiceUnavailable: 'Spraakmodus is nu niet beschikbaar.',
    errorNotConfigured: 'Spraakmodus is niet ingesteld op deze server.',
    errorPolicyOff: 'Je bedrijf heeft spraakmodus uitgeschakeld.',
    errorNoEngine: 'Er is nu geen spraakdienst voor spraakmodus beschikbaar.',
    errorTtsUnavailable: 'Voorlezen is nu niet beschikbaar.',
    errorPaymentRequired:
      'Het gebruikstegoed van je bedrijf is op. Een beheerder kan een kaart toevoegen; probeer het daarna opnieuw.',
    micHeldByVoice: 'De microfoon wordt gebruikt door spraakmodus.',
    voiceRefusedMeeting: 'Spraakmodus is uit zolang er een vergadering wordt opgenomen.',
    typeInstead: 'Typen',
    disclosureTitle: 'Praat met Poneva',
    disclosureBody:
      'Zolang spraakmodus aan staat, wordt wat je zegt naar onze spraak-naar-tekstdiensten ({processors}) gestuurd en wordt het je bericht.',
    disclosureReadAloud: 'Antwoorden die worden voorgelezen, krijgen hun stem van {processors}.',
    disclosureApprovals:
      'Je bericht wordt verzonden na een korte pauze die je ongedaan kunt maken. Goedkeuringen en wijzigingen in je gegevens wachten altijd tot jij ze bevestigt.',
    disclosureExit:
      'Tik op Verlaten, of vraag Poneva om spraakmodus te verlaten, wanneer je maar wilt.',
    turnOn: 'Inschakelen',
    notNow: 'Niet nu',
    degradedTapToSend:
      'Overgeschakeld naar een reservedienst voor transcriptie. Tik om te verzenden.',
    idleExited: 'Spraakmodus is gestopt na een tijd zonder spraak.',
    settingsTitle: 'Spraakmodus',
    handsFreeLabel: 'Handsfree',
    readAloudInVoice: 'Antwoorden voorlezen',
    speakWith: 'Antwoorden voorlezen met',
    speakWithPoneva: 'De stem van Poneva',
    screenReader: 'Mijn schermlezer',
    rate: 'Spreeksnelheid',
    spokenApprovalsLabel: 'Goedkeuringen met je stem bevestigen',
    companyVoiceMode: 'Spraakmodus',
    companyReadAloud: 'Voorlezen toestaan',
    companySpokenApprovals: 'Goedkeuren met stem toestaan',
    starting: 'Spraakmodus wordt gestart…',
    finishing: 'Bezig met afronden…',
    answering: 'Poneva antwoordt…',
    speakingInterrupt: 'Het antwoord wordt voorgelezen. Druk om te onderbreken.',
    stopReply: 'Stoppen met voorlezen',
    reconnecting: 'Verbinding wordt hersteld…',
    degraded: 'Overgeschakeld naar een reservedienst voor transcriptie.',
    micOn: 'Microfoon aan',
    pendingLabel: 'Je bericht, nog niet verzonden',
    pendingAnnouncement:
      'Je bericht is klaar om te verzenden. Enter verzendt het nu, Escape gooit het weg.',
    tapToSend: 'Tik op Nu verzenden om het te verzenden.',
    answerCardFirst: 'Beantwoord eerst de kaart hierboven, of tik op Nu verzenden.',
    noSpeechHeard: 'Er was geen spraak te horen.',
    replacedElsewhere: 'Spraakmodus is in een ander tabblad of een andere app gestart.',
    pausedHidden: 'Spraakmodus is gepauzeerd toen de pagina werd verborgen.',
    draftKept: 'Je niet-verzonden bericht is als concept bewaard.',
    errorPermission:
      'Sta de microfoon toe in de instellingen van je browser of apparaat en probeer het opnieuw.',
    errorNoMicrophone: 'Er is geen microfoon gevonden.',
    errorMicrophoneBusy: 'De microfoon wordt gebruikt door een andere app.',
    errorMicrophoneLost: 'De verbinding met de microfoon is verbroken.',
    errorInsecure: 'Spraakmodus vereist een beveiligde verbinding (https).',
    errorUnsupported: 'Deze browser kan geen geluid opnemen voor spraakmodus.',
    errorRateLimited: 'Spraakmodus is te vaak gestart. Probeer het over {seconds} s opnieuw.',
    errorBudget: 'Het tegoed voor spraakmodus van vandaag is op.',
    errorOffline: 'Je bent offline. Spraakmodus is gepauzeerd.',
    errorOriginNotAllowed:
      'Deze pagina-oorsprong is niet toegestaan voor de spraakmodus. Open Chat vanaf het goedgekeurde adres.',
    errorProviderUnavailable:
      'De spraakdienst is tijdelijk niet beschikbaar. Probeer het zo opnieuw.',
    errorSignIn: 'Log in om spraakmodus te gebruiken.',
    errorCompanyRequired: 'Spraakmodus is beschikbaar in een bedrijfswerkruimte.',
    errorGeneric: 'Spraakmodus is gestopt door een probleem. Probeer het opnieuw.',
    controlStop: 'Gestopt met voorlezen.',
    controlRepeat: 'Het antwoord wordt opnieuw voorgelezen.',
    controlExit: 'Spraakmodus verlaten.',
    controlRate: 'Spreeksnelheid ingesteld op {rate}×.',
    settingsDescription: 'Praat met Poneva in plaats van te typen, en hoor de antwoorden.',
    enabledLabel: 'Spraakmodus gebruiken',
    enabledHint: 'Als dit uit staat, legt Poneva spraakmodus uit voordat die start.',
    handsFreeHint: 'Poneva luistert en verzendt wanneer je pauzeert.',
    undoEndpoint: 'Nadat je stopt met praten (handsfree)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Beantwoord een bevestigingskaart door ja of nee te zeggen. Acties in gekoppelde diensten vragen altijd een tik.',
    saved: 'Opgeslagen',
    saveFailed: 'Je wijziging kon niet worden opgeslagen. Probeer het opnieuw.',
    policyTitle: 'Spraak en voorlezen',
    policyDescription: 'Deze instellingen gelden voor iedereen in je bedrijf.',
    companyVoiceModeHint:
      'Leden kunnen de spraakmodus gebruiken wanneer gesproken antwoorden beschikbaar zijn.',
    companyReadAloudHint: 'Poneva kan zijn antwoorden voorlezen.',
    companySpokenApprovalsHint:
      'Leden kunnen acties met hun stem bevestigen. Acties in gekoppelde diensten vragen altijd een tik.',
  },
  en,
  et: {
    voiceMode: 'Häälerežiim',
    enterVoice: 'Räägi Ponevaga',
    exitVoice: 'Välju häälerežiimist',
    listening: 'Kuulan…',
    hearing: 'Kuulen sind…',
    cancelSpeaking: 'Tühista',
    finishSpeaking: 'Valmis',
    sendingIn: 'Saatmine {seconds} s pärast',
    sendNow: 'Saada kohe',
    edit: 'Muuda',
    discard: 'Loobu',
    alwaysAsk: 'Küsi alati enne saatmist',
    undoWindow: 'Tagasivõtmise aken',
    settingsPageTitle: 'Hääl',
    waitBeforeSending: 'Ooteaeg enne saatmist',
    paused: 'Häälerežiim on peatatud.',
    resumeVoice: 'Jätka häälega',
    voiceUnavailable: 'Häälerežiim pole praegu saadaval.',
    errorNotConfigured: 'Häälerežiim pole selles serveris seadistatud.',
    errorPolicyOff: 'Sinu ettevõte on häälerežiimi välja lülitanud.',
    errorNoEngine: 'Häälerežiimi jaoks pole praegu ühtegi kõneteenust saadaval.',
    errorTtsUnavailable: 'Ettelugemine pole praegu saadaval.',
    errorPaymentRequired:
      'Sinu ettevõtte kasutuskrediit on otsas. Administraator saab lisada kaardi; seejärel proovi uuesti.',
    micHeldByVoice: 'Mikrofoni kasutab häälerežiim.',
    voiceRefusedMeeting: 'Häälerežiim on välja lülitatud, kuni koosolekut salvestatakse.',
    typeInstead: 'Kirjuta hoopis',
    disclosureTitle: 'Räägi Ponevaga',
    disclosureBody:
      'Kui häälerežiim on sees, saadetakse see, mida ütled, meie kõne tekstiks teisendamise teenusepakkujatele ({processors}) ja sellest saab sinu sõnum.',
    disclosureReadAloud: 'Ettelugetud vastused annab hääle {processors}.',
    disclosureApprovals:
      'Sinu sõnum saadetakse pärast lühikest pausi, mille saad tagasi võtta. Kinnitused ja sinu andmete muudatused ootavad alati, kuni sa need kinnitad.',
    disclosureExit: 'Puuduta igal ajal nuppu Välju või palu Poneval häälerežiimist väljuda.',
    turnOn: 'Lülita sisse',
    notNow: 'Mitte praegu',
    degradedTapToSend: 'Kasutusel on varu-transkriptsiooniteenus. Saatmiseks puuduta.',
    idleExited: 'Häälerežiim lõppes pärast vaikset hetke.',
    settingsTitle: 'Häälerežiim',
    handsFreeLabel: 'Käed-vabad',
    readAloudInVoice: 'Loe vastused ette',
    speakWith: 'Vastuste ettelugemiseks kasuta',
    speakWithPoneva: 'Poneva häält',
    screenReader: 'Minu ekraanilugejat',
    rate: 'Kõnekiirus',
    spokenApprovalsLabel: 'Kinnita heakskiidud häälega',
    companyVoiceMode: 'Häältrežiim',
    companyReadAloud: 'Luba ettelugemine',
    companySpokenApprovals: 'Luba heakskiitmine häälega',
    starting: 'Häälerežiimi käivitamine…',
    finishing: 'Lõpetamine…',
    answering: 'Poneva vastab…',
    speakingInterrupt: 'Vastust loetakse ette. Katkestamiseks vajuta.',
    stopReply: 'Lõpeta ettelugemine',
    reconnecting: 'Ühendan uuesti…',
    degraded: 'Kasutusel on varu-transkriptsiooniteenus.',
    micOn: 'Mikrofon on sees',
    pendingLabel: 'Sinu sõnum, veel saatmata',
    pendingAnnouncement:
      'Sinu sõnum on saatmiseks valmis. Enter saadab selle kohe, Escape loobub sellest.',
    tapToSend: 'Saatmiseks puuduta nuppu Saada kohe.',
    answerCardFirst: 'Vasta esmalt ülalolevale kaardile või puuduta nuppu Saada kohe.',
    noSpeechHeard: 'Kõnet ei kuuldud.',
    replacedElsewhere: 'Häälerežiim käivitati teisel vahekaardil või teises rakenduses.',
    pausedHidden: 'Häälerežiim peatati, kui leht peideti.',
    draftKept: 'Sinu saatmata sõnum salvestati mustandina.',
    errorPermission: 'Luba mikrofon brauseri või seadme seadetes ja proovi uuesti.',
    errorNoMicrophone: 'Mikrofoni ei leitud.',
    errorMicrophoneBusy: 'Mikrofoni kasutab teine rakendus.',
    errorMicrophoneLost: 'Mikrofoni ühendus katkes.',
    errorInsecure: 'Häälerežiim vajab turvalist ühendust (https).',
    errorUnsupported: 'See brauser ei saa häälerežiimi jaoks heli salvestada.',
    errorRateLimited: 'Häälerežiimi käivitati liiga sageli. Proovi uuesti {seconds} s pärast.',
    errorBudget: 'Tänane häälerežiimi kvoot on otsas.',
    errorOffline: 'Oled võrguühenduseta. Häälerežiim on peatatud.',
    errorOriginNotAllowed:
      'Selle lehe päritolu pole häälrežiimi jaoks lubatud. Ava Chat heakskiidetud aadressilt.',
    errorProviderUnavailable: 'Kõneteenus pole ajutiselt saadaval. Proovi varsti uuesti.',
    errorSignIn: 'Häälerežiimi kasutamiseks logi sisse.',
    errorCompanyRequired: 'Häälerežiim on saadaval ettevõtte tööruumis.',
    errorGeneric: 'Häälerežiim peatus probleemi tõttu. Proovi uuesti.',
    controlStop: 'Ettelugemine lõpetati.',
    controlRepeat: 'Vastust loetakse uuesti ette.',
    controlExit: 'Häälerežiimist väljuti.',
    controlRate: 'Kõnekiiruseks määrati {rate}×.',
    settingsDescription: 'Räägi Ponevaga kirjutamise asemel ja kuula vastuseid.',
    enabledLabel: 'Kasuta häälerežiimi',
    enabledHint: 'Kui see on väljas, selgitab Poneva häälerežiimi enne selle käivitumist.',
    handsFreeHint: 'Poneva kuulab ja saadab, kui teed pausi.',
    undoEndpoint: 'Pärast rääkimise lõpetamist (käed-vabad)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Vasta kinnituskaardile, öeldes jah või ei. Ühendatud teenuste toimingud vajavad alati puudutust.',
    saved: 'Salvestatud',
    saveFailed: 'Muudatust ei saanud salvestada. Proovi uuesti.',
    policyTitle: 'Hääl ja ettelugemine',
    policyDescription: 'Need seaded kehtivad kõigile sinu ettevõttes.',
    companyVoiceModeHint:
      'Liikmed saavad häältrežiimi kasutada, kui kõnelemisega vastused on saadaval.',
    companyReadAloudHint: 'Poneva saab oma vastuseid ette lugeda.',
    companySpokenApprovalsHint:
      'Liikmed saavad toiminguid häälega kinnitada. Ühendatud teenuste toimingud vajavad alati puudutust.',
  },
  fi: {
    voiceMode: 'Äänitila',
    enterVoice: 'Puhu Ponevalle',
    exitVoice: 'Poistu äänitilasta',
    listening: 'Kuunnellaan…',
    hearing: 'Kuulen sinut…',
    cancelSpeaking: 'Peruuta',
    finishSpeaking: 'Valmis',
    sendingIn: 'Lähetetään {seconds} s kuluttua',
    sendNow: 'Lähetä nyt',
    edit: 'Muokkaa',
    discard: 'Hylkää',
    alwaysAsk: 'Kysy aina ennen lähettämistä',
    undoWindow: 'Kumoamisaika',
    settingsPageTitle: 'Puhe',
    waitBeforeSending: 'Odotus ennen lähettämistä',
    paused: 'Äänitila on tauolla.',
    resumeVoice: 'Jatka äänellä',
    voiceUnavailable: 'Äänitila ei ole juuri nyt käytettävissä.',
    errorNotConfigured: 'Äänitilaa ei ole määritetty tälle palvelimelle.',
    errorPolicyOff: 'Yrityksesi on poistanut äänitilan käytöstä.',
    errorNoEngine: 'Äänitilalle ei ole juuri nyt saatavilla puhepalvelua.',
    errorTtsUnavailable: 'Ääneen lukeminen ei ole juuri nyt saatavilla.',
    errorPaymentRequired:
      'Yrityksesi käyttösaldo on loppunut. Järjestelmänvalvoja voi lisätä kortin, yritä sitten uudelleen.',
    micHeldByVoice: 'Äänitila käyttää mikrofonia.',
    voiceRefusedMeeting: 'Äänitila on pois käytöstä, kun kokousta tallennetaan.',
    typeInstead: 'Kirjoita mieluummin',
    disclosureTitle: 'Puhu Ponevalle',
    disclosureBody:
      'Kun äänitila on päällä, puheesi lähetetään puheentunnistuspalveluillemme ({processors}) ja siitä tulee viestisi.',
    disclosureReadAloud: 'Ääneen luettujen vastausten äänenä on {processors}.',
    disclosureApprovals:
      'Viestisi lähetetään lyhyen tauon jälkeen, jonka voit perua. Hyväksynnät ja tietojesi muutokset odottavat aina vahvistustasi.',
    disclosureExit: 'Napauta Poistu tai pyydä Ponevaa poistumaan äänitilasta milloin tahansa.',
    turnOn: 'Ota käyttöön',
    notNow: 'Ei nyt',
    degradedTapToSend: 'Vaihdettiin varalla olevaan litterointipalveluun. Lähetä napauttamalla.',
    idleExited: 'Äänitila päättyi hiljaisen hetken jälkeen.',
    settingsTitle: 'Äänitila',
    handsFreeLabel: 'Handsfree',
    readAloudInVoice: 'Lue vastaukset ääneen',
    speakWith: 'Lue vastaukset käyttäen',
    speakWithPoneva: 'Ponevan ääntä',
    screenReader: 'Ruudunlukuohjelmaani',
    rate: 'Puhenopeus',
    spokenApprovalsLabel: 'Vahvista hyväksynnät äänellä',
    companyVoiceMode: 'Äänitila',
    companyReadAloud: 'Salli ääneen lukeminen',
    companySpokenApprovals: 'Salli hyväksynnät äänellä',
    starting: 'Käynnistetään äänitilaa…',
    finishing: 'Viimeistellään…',
    answering: 'Poneva vastaa…',
    speakingInterrupt: 'Vastausta luetaan ääneen. Keskeytä painamalla.',
    stopReply: 'Lopeta ääneen lukeminen',
    reconnecting: 'Yhdistetään uudelleen…',
    degraded: 'Vaihdettiin varalla olevaan litterointipalveluun.',
    micOn: 'Mikrofoni päällä',
    pendingLabel: 'Viestisi, ei vielä lähetetty',
    pendingAnnouncement:
      'Viestisi on valmis lähetettäväksi. Enter lähettää sen nyt, Escape hylkää sen.',
    tapToSend: 'Lähetä se napauttamalla Lähetä nyt.',
    answerCardFirst: 'Vastaa ensin yllä olevaan korttiin tai napauta Lähetä nyt.',
    noSpeechHeard: 'Puhetta ei kuulunut.',
    replacedElsewhere: 'Äänitila käynnistettiin toisessa välilehdessä tai sovelluksessa.',
    pausedHidden: 'Äänitila keskeytettiin, kun sivu piilotettiin.',
    draftKept: 'Lähettämätön viestisi tallennettiin luonnokseksi.',
    errorPermission: 'Salli mikrofoni selaimen tai laitteen asetuksissa ja yritä uudelleen.',
    errorNoMicrophone: 'Mikrofonia ei löytynyt.',
    errorMicrophoneBusy: 'Toinen sovellus käyttää mikrofonia.',
    errorMicrophoneLost: 'Mikrofonin yhteys katkesi.',
    errorInsecure: 'Äänitila vaatii suojatun yhteyden (https).',
    errorUnsupported: 'Tämä selain ei voi tallentaa ääntä äänitilaa varten.',
    errorRateLimited: 'Äänitila käynnistettiin liian usein. Yritä uudelleen {seconds} s kuluttua.',
    errorBudget: 'Tämän päivän äänitilakiintiö on käytetty.',
    errorOffline: 'Olet offline-tilassa. Äänitila on tauolla.',
    errorOriginNotAllowed:
      'Tämän sivun alkuperää ei sallita äänitilassa. Avaa Chat hyväksytystä osoitteesta.',
    errorProviderUnavailable:
      'Puhepalvelu ei ole tilapäisesti käytettävissä. Yritä pian uudelleen.',
    errorSignIn: 'Kirjaudu sisään käyttääksesi äänitilaa.',
    errorCompanyRequired: 'Äänitila on käytettävissä yrityksen työtilassa.',
    errorGeneric: 'Äänitila pysähtyi ongelman takia. Yritä uudelleen.',
    controlStop: 'Ääneen lukeminen lopetettiin.',
    controlRepeat: 'Vastaus luetaan uudelleen.',
    controlExit: 'Äänitilasta poistuttiin.',
    controlRate: 'Puhenopeudeksi asetettiin {rate}×.',
    settingsDescription: 'Puhu Ponevalle kirjoittamisen sijaan ja kuuntele vastaukset.',
    enabledLabel: 'Käytä äänitilaa',
    enabledHint: 'Kun tämä on pois päältä, Poneva selittää äänitilan ennen sen alkamista.',
    handsFreeHint: 'Poneva kuuntelee ja lähettää, kun pidät tauon.',
    undoEndpoint: 'Kun lopetat puhumisen (handsfree)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Vastaa vahvistuskorttiin sanomalla kyllä tai ei. Yhdistettyjen palveluiden toiminnot vaativat aina napautuksen.',
    saved: 'Tallennettu',
    saveFailed: 'Muutosta ei voitu tallentaa. Yritä uudelleen.',
    policyTitle: 'Ääni ja ääneen lukeminen',
    policyDescription: 'Nämä asetukset koskevat kaikkia yrityksessäsi.',
    companyVoiceModeHint:
      'Jäsenet voivat käyttää äänitilaa, kun puhutut vastaukset ovat saatavilla.',
    companyReadAloudHint: 'Poneva voi lukea vastauksensa ääneen.',
    companySpokenApprovalsHint:
      'Jäsenet voivat vahvistaa toimintoja äänellä. Yhdistettyjen palveluiden toiminnot vaativat aina napautuksen.',
  },
  fr: {
    voiceMode: 'Mode vocal',
    enterVoice: 'Parler à Poneva',
    exitVoice: 'Quitter le mode vocal',
    listening: 'Écoute…',
    hearing: 'Je vous entends…',
    cancelSpeaking: 'Annuler',
    finishSpeaking: 'J’ai fini de parler',
    sendingIn: 'Envoi dans {seconds} s',
    sendNow: 'Envoyer maintenant',
    edit: 'Modifier',
    discard: 'Supprimer',
    alwaysAsk: 'Toujours demander avant d’envoyer',
    undoWindow: 'Délai d’annulation',
    settingsPageTitle: 'Voix',
    waitBeforeSending: 'Attente avant l’envoi',
    paused: 'Le mode vocal est en pause.',
    resumeVoice: 'Reprendre la voix',
    voiceUnavailable: 'Le mode vocal n’est pas disponible pour le moment.',
    errorNotConfigured: 'Le mode vocal n’est pas configuré sur ce serveur.',
    errorPolicyOff: 'Votre entreprise a désactivé le mode vocal.',
    errorNoEngine: 'Aucun service vocal n’est disponible pour le mode vocal pour le moment.',
    errorTtsUnavailable: 'La lecture à voix haute est indisponible pour le moment.',
    errorPaymentRequired:
      'Le crédit d’utilisation de votre entreprise est épuisé. Un administrateur peut ajouter une carte ; réessayez ensuite.',
    micHeldByVoice: 'Le micro est utilisé par le mode vocal.',
    voiceRefusedMeeting: 'Le mode vocal est désactivé pendant l’enregistrement d’une réunion.',
    typeInstead: 'Écrire plutôt',
    disclosureTitle: 'Parler à Poneva',
    disclosureBody:
      'Lorsque le mode vocal est activé, ce que vous dites est envoyé à nos fournisseurs de reconnaissance vocale ({processors}) et devient votre message.',
    disclosureReadAloud: 'Les réponses lues à voix haute sont prononcées par {processors}.',
    disclosureApprovals:
      'Votre message est envoyé après une courte pause que vous pouvez annuler. Les approbations et les modifications de vos données attendent toujours votre confirmation.',
    disclosureExit:
      'Touchez Quitter, ou demandez à Poneva de quitter le mode vocal, à tout moment.',
    turnOn: 'Activer',
    notNow: 'Pas maintenant',
    degradedTapToSend: 'Passage à un service de transcription de secours. Touchez pour envoyer.',
    idleExited: 'Le mode vocal s’est arrêté après un moment de silence.',
    settingsTitle: 'Mode vocal',
    handsFreeLabel: 'Mains libres',
    readAloudInVoice: 'Lire les réponses à voix haute',
    speakWith: 'Lire les réponses avec',
    speakWithPoneva: 'La voix de Poneva',
    screenReader: 'Mon lecteur d’écran',
    rate: 'Débit de parole',
    spokenApprovalsLabel: 'Confirmer les approbations à la voix',
    companyVoiceMode: 'Mode vocal',
    companyReadAloud: 'Autoriser la lecture à voix haute',
    companySpokenApprovals: 'Autoriser les approbations à la voix',
    starting: 'Démarrage du mode vocal…',
    finishing: 'Finalisation…',
    answering: 'Poneva répond…',
    speakingInterrupt: 'Lecture de la réponse. Appuyez pour interrompre.',
    stopReply: 'Arrêter la lecture à voix haute',
    reconnecting: 'Reconnexion…',
    degraded: 'Passage à un service de transcription de secours.',
    micOn: 'Micro activé',
    pendingLabel: 'Votre message, pas encore envoyé',
    pendingAnnouncement:
      'Votre message est prêt à être envoyé. Entrée l’envoie maintenant, Échap le supprime.',
    tapToSend: 'Touchez Envoyer maintenant pour l’envoyer.',
    answerCardFirst: 'Répondez d’abord à la carte ci-dessus, ou touchez Envoyer maintenant.',
    noSpeechHeard: 'Aucune parole n’a été détectée.',
    replacedElsewhere: 'Le mode vocal a démarré dans un autre onglet ou une autre application.',
    pausedHidden: 'Le mode vocal a été mis en pause quand la page a été masquée.',
    draftKept: 'Votre message non envoyé a été conservé comme brouillon.',
    errorPermission:
      'Autorisez le micro dans les réglages du navigateur ou de l’appareil, puis réessayez.',
    errorNoMicrophone: 'Aucun micro n’a été trouvé.',
    errorMicrophoneBusy: 'Le micro est utilisé par une autre application.',
    errorMicrophoneLost: 'Le micro a été déconnecté.',
    errorInsecure: 'Le mode vocal nécessite une connexion sécurisée (https).',
    errorUnsupported: 'Ce navigateur ne peut pas enregistrer de son pour le mode vocal.',
    errorRateLimited: 'Le mode vocal a été lancé trop souvent. Réessayez dans {seconds} s.',
    errorBudget: 'Le quota du mode vocal pour aujourd’hui est épuisé.',
    errorOffline: 'Vous êtes hors ligne. Le mode vocal est en pause.',
    errorOriginNotAllowed:
      'L’origine de cette page n’est pas autorisée pour le mode vocal. Ouvrez Chat depuis l’adresse approuvée.',
    errorProviderUnavailable:
      'Le service vocal est temporairement indisponible. Réessayez bientôt.',
    errorSignIn: 'Connectez-vous pour utiliser le mode vocal.',
    errorCompanyRequired: 'Le mode vocal est disponible dans un espace de travail d’entreprise.',
    errorGeneric: 'Le mode vocal s’est arrêté à cause d’un problème. Réessayez.',
    controlStop: 'Lecture à voix haute arrêtée.',
    controlRepeat: 'Nouvelle lecture de la réponse.',
    controlExit: 'Mode vocal quitté.',
    controlRate: 'Débit de parole réglé sur {rate}×.',
    settingsDescription: 'Parlez à Poneva au lieu d’écrire, et écoutez ses réponses.',
    enabledLabel: 'Utiliser le mode vocal',
    enabledHint:
      'Si cette option est désactivée, Poneva explique le mode vocal avant qu’il ne démarre.',
    handsFreeHint: 'Poneva écoute et envoie quand vous faites une pause.',
    undoEndpoint: 'Après avoir cessé de parler (mains libres)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Répondez à une carte de confirmation en disant oui ou non. Les actions dans les services connectés demandent toujours un toucher.',
    saved: 'Enregistré',
    saveFailed: 'Votre modification n’a pas pu être enregistrée. Réessayez.',
    policyTitle: 'Voix et lecture à voix haute',
    policyDescription: 'Ces réglages s’appliquent à tous les membres de votre entreprise.',
    companyVoiceModeHint:
      'Les membres peuvent utiliser le mode vocal lorsque les réponses vocales sont disponibles.',
    companyReadAloudHint: 'Poneva peut lire ses réponses à voix haute.',
    companySpokenApprovalsHint:
      'Les membres peuvent confirmer des actions à la voix. Les actions dans les services connectés demandent toujours un toucher.',
  },
  de: {
    voiceMode: 'Sprachmodus',
    enterVoice: 'Mit Poneva sprechen',
    exitVoice: 'Sprachmodus beenden',
    listening: 'Hört zu…',
    hearing: 'Hört Sie…',
    cancelSpeaking: 'Abbrechen',
    finishSpeaking: 'Fertig gesprochen',
    sendingIn: 'Wird in {seconds} s gesendet',
    sendNow: 'Jetzt senden',
    edit: 'Bearbeiten',
    discard: 'Verwerfen',
    alwaysAsk: 'Vor dem Senden immer fragen',
    undoWindow: 'Rückgängig-Fenster',
    settingsPageTitle: 'Stimme',
    waitBeforeSending: 'Wartezeit vor dem Senden',
    paused: 'Der Sprachmodus ist pausiert.',
    resumeVoice: 'Sprache fortsetzen',
    voiceUnavailable: 'Der Sprachmodus ist gerade nicht verfügbar.',
    errorNotConfigured: 'Der Sprachmodus ist auf diesem Server nicht eingerichtet.',
    errorPolicyOff: 'Ihr Unternehmen hat den Sprachmodus deaktiviert.',
    errorNoEngine: 'Gerade ist kein Sprachdienst für den Sprachmodus verfügbar.',
    errorTtsUnavailable: 'Vorlesen ist gerade nicht verfügbar.',
    errorPaymentRequired:
      'Das Nutzungsguthaben Ihres Unternehmens ist aufgebraucht. Ein Administrator kann eine Karte hinzufügen; versuchen Sie es dann erneut.',
    micHeldByVoice: 'Das Mikrofon wird vom Sprachmodus verwendet.',
    voiceRefusedMeeting: 'Der Sprachmodus ist aus, solange ein Meeting aufgenommen wird.',
    typeInstead: 'Stattdessen tippen',
    disclosureTitle: 'Mit Poneva sprechen',
    disclosureBody:
      'Solange der Sprachmodus aktiv ist, wird Ihre Sprache an unsere Sprache-zu-Text-Anbieter ({processors}) gesendet und wird zu Ihrer Nachricht.',
    disclosureReadAloud: 'Vorgelesene Antworten werden von {processors} gesprochen.',
    disclosureApprovals:
      'Ihre Nachricht wird nach einer kurzen Pause gesendet, die Sie rückgängig machen können. Freigaben und Änderungen an Ihren Daten warten immer auf Ihre Bestätigung.',
    disclosureExit:
      'Tippen Sie jederzeit auf Beenden oder bitten Sie Poneva, den Sprachmodus zu verlassen.',
    turnOn: 'Einschalten',
    notNow: 'Nicht jetzt',
    degradedTapToSend: 'Zu einem Ersatzdienst für die Transkription gewechselt. Zum Senden tippen.',
    idleExited: 'Der Sprachmodus wurde nach einer ruhigen Weile beendet.',
    settingsTitle: 'Sprachmodus',
    handsFreeLabel: 'Freihändig',
    readAloudInVoice: 'Antworten vorlesen',
    speakWith: 'Antworten vorlesen mit',
    speakWithPoneva: 'Ponevas Stimme',
    screenReader: 'Mein Screenreader',
    rate: 'Sprechgeschwindigkeit',
    spokenApprovalsLabel: 'Freigaben per Sprache bestätigen',
    companyVoiceMode: 'Sprachmodus',
    companyReadAloud: 'Vorlesen erlauben',
    companySpokenApprovals: 'Freigaben per Sprache erlauben',
    starting: 'Sprachmodus wird gestartet…',
    finishing: 'Wird abgeschlossen…',
    answering: 'Poneva antwortet…',
    speakingInterrupt: 'Die Antwort wird vorgelesen. Zum Unterbrechen drücken.',
    stopReply: 'Vorlesen beenden',
    reconnecting: 'Verbindung wird wiederhergestellt…',
    degraded: 'Zu einem Ersatzdienst für die Transkription gewechselt.',
    micOn: 'Mikrofon an',
    pendingLabel: 'Ihre Nachricht, noch nicht gesendet',
    pendingAnnouncement:
      'Ihre Nachricht ist bereit zum Senden. Die Eingabetaste sendet sie jetzt, Escape verwirft sie.',
    tapToSend: 'Tippen Sie auf Jetzt senden, um sie zu senden.',
    answerCardFirst: 'Beantworten Sie zuerst die Karte oben, oder tippen Sie auf Jetzt senden.',
    noSpeechHeard: 'Es wurde keine Sprache erkannt.',
    replacedElsewhere:
      'Der Sprachmodus wurde in einem anderen Tab oder einer anderen App gestartet.',
    pausedHidden: 'Der Sprachmodus wurde pausiert, als die Seite ausgeblendet wurde.',
    draftKept: 'Ihre nicht gesendete Nachricht wurde als Entwurf behalten.',
    errorPermission:
      'Erlauben Sie das Mikrofon in den Browser- oder Geräteeinstellungen und versuchen Sie es erneut.',
    errorNoMicrophone: 'Es wurde kein Mikrofon gefunden.',
    errorMicrophoneBusy: 'Das Mikrofon wird von einer anderen App verwendet.',
    errorMicrophoneLost: 'Die Verbindung zum Mikrofon wurde getrennt.',
    errorInsecure: 'Der Sprachmodus erfordert eine sichere Verbindung (https).',
    errorUnsupported: 'Dieser Browser kann keinen Ton für den Sprachmodus aufnehmen.',
    errorRateLimited:
      'Der Sprachmodus wurde zu oft gestartet. Versuchen Sie es in {seconds} s erneut.',
    errorBudget: 'Das heutige Kontingent für den Sprachmodus ist aufgebraucht.',
    errorOffline: 'Sie sind offline. Der Sprachmodus ist pausiert.',
    errorOriginNotAllowed:
      'Diese Seitenherkunft ist für den Sprachmodus nicht zugelassen. Öffnen Sie Chat über die genehmigte Adresse.',
    errorProviderUnavailable:
      'Der Sprachdienst ist vorübergehend nicht verfügbar. Versuchen Sie es bald erneut.',
    errorSignIn: 'Melden Sie sich an, um den Sprachmodus zu verwenden.',
    errorCompanyRequired: 'Der Sprachmodus ist in einem Firmen-Arbeitsbereich verfügbar.',
    errorGeneric: 'Der Sprachmodus wurde wegen eines Problems beendet. Versuchen Sie es erneut.',
    controlStop: 'Vorlesen beendet.',
    controlRepeat: 'Die Antwort wird erneut vorgelesen.',
    controlExit: 'Sprachmodus verlassen.',
    controlRate: 'Sprechgeschwindigkeit auf {rate}× gesetzt.',
    settingsDescription: 'Sprechen Sie mit Poneva, statt zu tippen, und hören Sie die Antworten.',
    enabledLabel: 'Sprachmodus verwenden',
    enabledHint: 'Wenn dies aus ist, erklärt Poneva den Sprachmodus, bevor er startet.',
    handsFreeHint: 'Poneva hört zu und sendet, wenn Sie eine Pause machen.',
    undoEndpoint: 'Nachdem Sie aufhören zu sprechen (freihändig)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Beantworten Sie eine Bestätigungskarte mit Ja oder Nein. Aktionen in verbundenen Diensten erfordern immer ein Tippen.',
    saved: 'Gespeichert',
    saveFailed: 'Ihre Änderung konnte nicht gespeichert werden. Versuchen Sie es erneut.',
    policyTitle: 'Sprache und Vorlesen',
    policyDescription: 'Diese Schalter gelten für alle in Ihrem Unternehmen.',
    companyVoiceModeHint:
      'Mitglieder können den Sprachmodus nutzen, wenn gesprochene Antworten verfügbar sind.',
    companyReadAloudHint: 'Poneva kann seine Antworten vorlesen.',
    companySpokenApprovalsHint:
      'Mitglieder können Aktionen per Sprache bestätigen. Aktionen in verbundenen Diensten erfordern immer ein Tippen.',
  },
  el: {
    voiceMode: 'Λειτουργία φωνής',
    enterVoice: 'Μιλήστε με το Poneva',
    exitVoice: 'Έξοδος από τη λειτουργία φωνής',
    listening: 'Ακρόαση…',
    hearing: 'Σας ακούω…',
    cancelSpeaking: 'Ακύρωση',
    finishSpeaking: 'Τέλος ομιλίας',
    sendingIn: 'Αποστολή σε {seconds} δ',
    sendNow: 'Αποστολή τώρα',
    edit: 'Επεξεργασία',
    discard: 'Απόρριψη',
    alwaysAsk: 'Να ρωτάει πάντα πριν από την αποστολή',
    undoWindow: 'Παράθυρο αναίρεσης',
    settingsPageTitle: 'Φωνή',
    waitBeforeSending: 'Αναμονή πριν την αποστολή',
    paused: 'Η λειτουργία φωνής είναι σε παύση.',
    resumeVoice: 'Συνέχεια με φωνή',
    voiceUnavailable: 'Η λειτουργία φωνής δεν είναι διαθέσιμη αυτή τη στιγμή.',
    errorNotConfigured: 'Η λειτουργία φωνής δεν έχει ρυθμιστεί σε αυτόν τον διακομιστή.',
    errorPolicyOff: 'Η εταιρεία σας έχει απενεργοποιήσει τη λειτουργία φωνής.',
    errorNoEngine: 'Δεν υπάρχει διαθέσιμη υπηρεσία ομιλίας για τη λειτουργία φωνής αυτή τη στιγμή.',
    errorTtsUnavailable: 'Η εκφώνηση δεν είναι διαθέσιμη αυτή τη στιγμή.',
    errorPaymentRequired:
      'Η πίστωση χρήσης της εταιρείας σας έχει εξαντληθεί. Ένας διαχειριστής μπορεί να προσθέσει κάρτα· μετά δοκιμάστε ξανά.',
    micHeldByVoice: 'Το μικρόφωνο χρησιμοποιείται από τη λειτουργία φωνής.',
    voiceRefusedMeeting: 'Η λειτουργία φωνής είναι ανενεργή όσο ηχογραφείται μια σύσκεψη.',
    typeInstead: 'Πληκτρολόγηση αντί για φωνή',
    disclosureTitle: 'Μιλήστε με το Poneva',
    disclosureBody:
      'Όσο η λειτουργία φωνής είναι ενεργή, ό,τι λέτε αποστέλλεται στους παρόχους μετατροπής ομιλίας σε κείμενο ({processors}) και γίνεται το μήνυμά σας.',
    disclosureReadAloud: 'Οι απαντήσεις που διαβάζονται φωναχτά εκφωνούνται από το {processors}.',
    disclosureApprovals:
      'Το μήνυμά σας αποστέλλεται μετά από μια σύντομη παύση που μπορείτε να αναιρέσετε. Οι εγκρίσεις και οι αλλαγές στα δεδομένα σας περιμένουν πάντα την επιβεβαίωσή σας.',
    disclosureExit:
      'Πατήστε Έξοδος ή ζητήστε από το Poneva να βγει από τη λειτουργία φωνής, όποτε θέλετε.',
    turnOn: 'Ενεργοποίηση',
    notNow: 'Όχι τώρα',
    degradedTapToSend: 'Έγινε μετάβαση σε εφεδρική υπηρεσία μεταγραφής. Πατήστε για αποστολή.',
    idleExited: 'Η λειτουργία φωνής τερματίστηκε μετά από λίγη ησυχία.',
    settingsTitle: 'Λειτουργία φωνής',
    handsFreeLabel: 'Χωρίς χέρια',
    readAloudInVoice: 'Ανάγνωση απαντήσεων φωναχτά',
    speakWith: 'Ανάγνωση απαντήσεων με',
    speakWithPoneva: 'Τη φωνή του Poneva',
    screenReader: 'Τον αναγνώστη οθόνης μου',
    rate: 'Ταχύτητα ομιλίας',
    spokenApprovalsLabel: 'Επιβεβαίωση εγκρίσεων με φωνή',
    companyVoiceMode: 'Λειτουργία φωνής',
    companyReadAloud: 'Να επιτρέπεται η ανάγνωση φωναχτά',
    companySpokenApprovals: 'Να επιτρέπονται εγκρίσεις με φωνή',
    starting: 'Εκκίνηση λειτουργίας φωνής…',
    finishing: 'Ολοκλήρωση…',
    answering: 'Το Poneva απαντά…',
    speakingInterrupt: 'Ανάγνωση της απάντησης. Πατήστε για διακοπή.',
    stopReply: 'Διακοπή ανάγνωσης φωναχτά',
    reconnecting: 'Επανασύνδεση…',
    degraded: 'Έγινε μετάβαση σε εφεδρική υπηρεσία μεταγραφής.',
    micOn: 'Μικρόφωνο ενεργό',
    pendingLabel: 'Το μήνυμά σας, δεν έχει σταλεί ακόμη',
    pendingAnnouncement:
      'Το μήνυμά σας είναι έτοιμο για αποστολή. Το Enter το στέλνει τώρα, το Escape το απορρίπτει.',
    tapToSend: 'Πατήστε Αποστολή τώρα για να το στείλετε.',
    answerCardFirst: 'Απαντήστε πρώτα στην κάρτα παραπάνω ή πατήστε Αποστολή τώρα.',
    noSpeechHeard: 'Δεν ακούστηκε ομιλία.',
    replacedElsewhere: 'Η λειτουργία φωνής ξεκίνησε σε άλλη καρτέλα ή εφαρμογή.',
    pausedHidden: 'Η λειτουργία φωνής τέθηκε σε παύση όταν η σελίδα κρύφτηκε.',
    draftKept: 'Το μήνυμα που δεν στάλθηκε κρατήθηκε ως πρόχειρο.',
    errorPermission:
      'Επιτρέψτε το μικρόφωνο στις ρυθμίσεις του προγράμματος περιήγησης ή της συσκευής και δοκιμάστε ξανά.',
    errorNoMicrophone: 'Δεν βρέθηκε μικρόφωνο.',
    errorMicrophoneBusy: 'Το μικρόφωνο χρησιμοποιείται από άλλη εφαρμογή.',
    errorMicrophoneLost: 'Το μικρόφωνο αποσυνδέθηκε.',
    errorInsecure: 'Η λειτουργία φωνής απαιτεί ασφαλή σύνδεση (https).',
    errorUnsupported:
      'Αυτό το πρόγραμμα περιήγησης δεν μπορεί να ηχογραφήσει ήχο για τη λειτουργία φωνής.',
    errorRateLimited: 'Η λειτουργία φωνής ξεκίνησε πολύ συχνά. Δοκιμάστε ξανά σε {seconds} δ.',
    errorBudget: 'Το σημερινό όριο της λειτουργίας φωνής εξαντλήθηκε.',
    errorOffline: 'Είστε εκτός σύνδεσης. Η λειτουργία φωνής είναι σε παύση.',
    errorOriginNotAllowed:
      'Η προέλευση αυτής της σελίδας δεν επιτρέπεται για τη λειτουργία φωνής. Ανοίξτε το Chat από την εγκεκριμένη διεύθυνση.',
    errorProviderUnavailable:
      'Η υπηρεσία ομιλίας δεν είναι προσωρινά διαθέσιμη. Δοκιμάστε ξανά σύντομα.',
    errorSignIn: 'Συνδεθείτε για να χρησιμοποιήσετε τη λειτουργία φωνής.',
    errorCompanyRequired: 'Η λειτουργία φωνής είναι διαθέσιμη σε χώρο εργασίας εταιρείας.',
    errorGeneric: 'Η λειτουργία φωνής σταμάτησε λόγω προβλήματος. Δοκιμάστε ξανά.',
    controlStop: 'Η ανάγνωση φωναχτά σταμάτησε.',
    controlRepeat: 'Η απάντηση διαβάζεται ξανά.',
    controlExit: 'Έξοδος από τη λειτουργία φωνής.',
    controlRate: 'Η ταχύτητα ομιλίας ορίστηκε σε {rate}×.',
    settingsDescription: 'Μιλήστε με το Poneva αντί να πληκτρολογείτε και ακούστε τις απαντήσεις.',
    enabledLabel: 'Χρήση λειτουργίας φωνής',
    enabledHint:
      'Όταν είναι απενεργοποιημένο, το Poneva εξηγεί τη λειτουργία φωνής πριν ξεκινήσει.',
    handsFreeHint: 'Το Poneva ακούει και στέλνει όταν κάνετε παύση.',
    undoEndpoint: 'Αφού σταματήσετε να μιλάτε (χωρίς χέρια)',
    undoSeconds: '{seconds} δ',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Απαντήστε σε μια κάρτα επιβεβαίωσης λέγοντας ναι ή όχι. Οι ενέργειες σε συνδεδεμένες υπηρεσίες απαιτούν πάντα πάτημα.',
    saved: 'Αποθηκεύτηκε',
    saveFailed: 'Η αλλαγή σας δεν αποθηκεύτηκε. Δοκιμάστε ξανά.',
    policyTitle: 'Φωνή και ανάγνωση φωναχτά',
    policyDescription: 'Αυτές οι ρυθμίσεις ισχύουν για όλους στην εταιρεία σας.',
    companyVoiceModeHint:
      'Τα μέλη μπορούν να χρησιμοποιούν τη λειτουργία φωνής όταν είναι διαθέσιμες οι φωνητικές απαντήσεις.',
    companyReadAloudHint: 'Το Poneva μπορεί να διαβάζει φωναχτά τις απαντήσεις του.',
    companySpokenApprovalsHint:
      'Τα μέλη μπορούν να επιβεβαιώνουν ενέργειες με τη φωνή τους. Οι ενέργειες σε συνδεδεμένες υπηρεσίες απαιτούν πάντα πάτημα.',
  },
  hu: {
    voiceMode: 'Hangmód',
    enterVoice: 'Beszéljen a Ponevával',
    exitVoice: 'Kilépés a hangmódból',
    listening: 'Figyelek…',
    hearing: 'Hallom önt…',
    cancelSpeaking: 'Mégse',
    finishSpeaking: 'Kész',
    sendingIn: 'Küldés {seconds} mp múlva',
    sendNow: 'Küldés most',
    edit: 'Szerkesztés',
    discard: 'Elvetés',
    alwaysAsk: 'Küldés előtt mindig kérdezzen',
    undoWindow: 'Visszavonási idő',
    settingsPageTitle: 'Hang',
    waitBeforeSending: 'Várakozás küldés előtt',
    paused: 'A hangmód szünetel.',
    resumeVoice: 'Hang folytatása',
    voiceUnavailable: 'A hangmód most nem érhető el.',
    errorNotConfigured: 'A hangmód nincs beállítva ezen a szerveren.',
    errorPolicyOff: 'A cége kikapcsolta a hangmódot.',
    errorNoEngine: 'Most nem érhető el beszédszolgáltatás a hangmódhoz.',
    errorTtsUnavailable: 'A felolvasás most nem érhető el.',
    errorPaymentRequired:
      'A cég használati kerete elfogyott. Egy rendszergazda hozzáadhat egy kártyát, utána próbálja újra.',
    micHeldByVoice: 'A mikrofont a hangmód használja.',
    voiceRefusedMeeting: 'A hangmód ki van kapcsolva, amíg egy értekezlet felvétele zajlik.',
    typeInstead: 'Inkább gépelek',
    disclosureTitle: 'Beszéljen a Ponevával',
    disclosureBody:
      'Amíg a hangmód be van kapcsolva, amit mond, az a beszédfelismerő szolgáltatóinkhoz ({processors}) kerül, és ebből lesz az üzenete.',
    disclosureReadAloud: 'A felolvasott válaszok hangját az {processors} adja.',
    disclosureApprovals:
      'Az üzenet egy rövid szünet után megy el, amelyet visszavonhat. A jóváhagyások és az adatai módosításai mindig az ön megerősítésére várnak.',
    disclosureExit:
      'Bármikor koppintson a Kilépés gombra, vagy kérje meg a Ponevát, hogy lépjen ki a hangmódból.',
    turnOn: 'Bekapcsolás',
    notNow: 'Most nem',
    degradedTapToSend: 'Átváltva egy tartalék átírási szolgáltatásra. Koppintson a küldéshez.',
    idleExited: 'A hangmód egy csendes időszak után befejeződött.',
    settingsTitle: 'Hangmód',
    handsFreeLabel: 'Kihangosított',
    readAloudInVoice: 'Válaszok felolvasása',
    speakWith: 'Válaszok felolvasása ezzel',
    speakWithPoneva: 'A Poneva hangja',
    screenReader: 'A saját képernyőolvasóm',
    rate: 'Beszédsebesség',
    spokenApprovalsLabel: 'Jóváhagyások megerősítése hanggal',
    companyVoiceMode: 'Hangmód',
    companyReadAloud: 'Felolvasás engedélyezése',
    companySpokenApprovals: 'Hangalapú jóváhagyás engedélyezése',
    starting: 'Hangmód indítása…',
    finishing: 'Befejezés…',
    answering: 'A Poneva válaszol…',
    speakingInterrupt: 'A válasz felolvasása folyik. Nyomja meg a megszakításhoz.',
    stopReply: 'Felolvasás leállítása',
    reconnecting: 'Újracsatlakozás…',
    degraded: 'Átváltva egy tartalék átírási szolgáltatásra.',
    micOn: 'Mikrofon bekapcsolva',
    pendingLabel: 'Az üzenete, még nincs elküldve',
    pendingAnnouncement: 'Az üzenet küldésre kész. Az Enter most elküldi, az Escape elveti.',
    tapToSend: 'A küldéshez koppintson a Küldés most gombra.',
    answerCardFirst: 'Előbb válaszoljon a fenti kártyára, vagy koppintson a Küldés most gombra.',
    noSpeechHeard: 'Nem hallatszott beszéd.',
    replacedElsewhere: 'A hangmód egy másik lapon vagy alkalmazásban indult el.',
    pausedHidden: 'A hangmód szünetelt, amikor az oldal el lett rejtve.',
    draftKept: 'Az el nem küldött üzenetét piszkozatként megőriztük.',
    errorPermission:
      'Engedélyezze a mikrofont a böngésző vagy az eszköz beállításaiban, és próbálja újra.',
    errorNoMicrophone: 'Nem található mikrofon.',
    errorMicrophoneBusy: 'A mikrofont egy másik alkalmazás használja.',
    errorMicrophoneLost: 'A mikrofon kapcsolata megszakadt.',
    errorInsecure: 'A hangmódhoz biztonságos kapcsolat (https) szükséges.',
    errorUnsupported: 'Ez a böngésző nem tud hangot rögzíteni a hangmódhoz.',
    errorRateLimited: 'A hangmódot túl gyakran indították el. Próbálja újra {seconds} mp múlva.',
    errorBudget: 'A mai hangmódkeret elfogyott.',
    errorOffline: 'Nincs internetkapcsolat. A hangmód szünetel.',
    errorOriginNotAllowed:
      'Ez az oldalforrás nem engedélyezett hangmódhoz. Nyissa meg a Chatet a jóváhagyott címről.',
    errorProviderUnavailable:
      'A beszédszolgáltatás átmenetileg nem érhető el. Próbálja újra hamarosan.',
    errorSignIn: 'Jelentkezzen be a hangmód használatához.',
    errorCompanyRequired: 'A hangmód céges munkaterületen érhető el.',
    errorGeneric: 'A hangmód egy hiba miatt leállt. Próbálja újra.',
    controlStop: 'A felolvasás leállt.',
    controlRepeat: 'A válasz újbóli felolvasása.',
    controlExit: 'Kilépett a hangmódból.',
    controlRate: 'A beszédsebesség {rate}× lett.',
    settingsDescription: 'Beszéljen a Ponevával gépelés helyett, és hallgassa meg a válaszokat.',
    enabledLabel: 'Hangmód használata',
    enabledHint: 'Ha ez ki van kapcsolva, a Poneva indítás előtt elmagyarázza a hangmódot.',
    handsFreeHint: 'A Poneva figyel, és szünetnél elküldi az üzenetet.',
    undoEndpoint: 'Miután abbahagyja a beszédet (kihangosított)',
    undoSeconds: '{seconds} mp',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Válaszoljon egy megerősítő kártyára igennel vagy nemmel. A csatlakoztatott szolgáltatásokban végzett műveletekhez mindig koppintás kell.',
    saved: 'Mentve',
    saveFailed: 'A módosítást nem sikerült menteni. Próbálja újra.',
    policyTitle: 'Hang és felolvasás',
    policyDescription: 'Ezek a beállítások a cég minden tagjára vonatkoznak.',
    companyVoiceModeHint:
      'A tagok akkor használhatják a hangmódot, ha a hangos válaszok elérhetők.',
    companyReadAloudHint: 'A Poneva felolvashatja a válaszait.',
    companySpokenApprovalsHint:
      'A tagok hanggal erősíthetnek meg műveleteket. A csatlakoztatott szolgáltatásokban végzett műveletekhez mindig koppintás kell.',
  },
  ga: {
    voiceMode: 'Mód gutha',
    enterVoice: 'Labhair le Poneva',
    exitVoice: 'Scoir den mhód gutha',
    listening: 'Ag éisteacht…',
    hearing: 'Cloisim thú…',
    cancelSpeaking: 'Cealaigh',
    finishSpeaking: 'Críochnaithe',
    sendingIn: 'Á sheoladh i gceann {seconds} s',
    sendNow: 'Seol anois',
    edit: 'Cuir in eagar',
    discard: 'Caith uait',
    alwaysAsk: 'Fiafraigh i gcónaí roimh sheoladh',
    undoWindow: 'Fuinneog chealaithe',
    settingsPageTitle: 'Guth',
    waitBeforeSending: 'Fanacht roimh sheoladh',
    paused: 'Tá an mód gutha ar sos.',
    resumeVoice: 'Lean ar aghaidh le guth',
    voiceUnavailable: 'Níl an mód gutha ar fáil faoi láthair.',
    errorNotConfigured: 'Níl an mód gutha socraithe ar an bhfreastalaí seo.',
    errorPolicyOff: 'Tá an mód gutha múchta ag do chuideachta.',
    errorNoEngine: 'Níl aon seirbhís urlabhra ar fáil don mhód gutha faoi láthair.',
    errorTtsUnavailable: 'Níl léamh os ard ar fáil faoi láthair.',
    errorPaymentRequired:
      'Tá creidmheas úsáide do chuideachta ídithe. Is féidir le riarthóir cárta a chur leis; ansin bain triail eile as.',
    micHeldByVoice: 'Tá an micreafón in úsáid ag an mód gutha.',
    voiceRefusedMeeting: 'Tá an mód gutha múchta fad is atá cruinniú á thaifeadadh.',
    typeInstead: 'Clóscríobh ina ionad',
    disclosureTitle: 'Labhair le Poneva',
    disclosureBody:
      'Fad is atá an mód gutha ar siúl, seoltar an méid a deir tú chuig ár soláthraithe urlabhra go téacs ({processors}) agus is é do theachtaireacht é.',
    disclosureReadAloud: 'Is iad {processors} a thugann guth do na freagraí a léitear os ard.',
    disclosureApprovals:
      'Seoltar do theachtaireacht tar éis sos gearr is féidir leat a chealú. Fanann faomhadh agus athruithe ar do chuid sonraí i gcónaí go ndearbhaíonn tú iad.',
    disclosureExit: 'Tapáil Scoir, nó iarr ar Poneva an mód gutha a fhágáil, am ar bith.',
    turnOn: 'Cas air',
    notNow: 'Ní anois',
    degradedTapToSend: 'Aistríodh chuig seirbhís tras-scríofa chúltaca. Tapáil chun seoladh.',
    idleExited: 'Chríochnaigh an mód gutha tar éis tamaill chiúin.',
    settingsTitle: 'Mód gutha',
    handsFreeLabel: 'Gan lámha',
    readAloudInVoice: 'Léigh freagraí os ard',
    speakWith: 'Léigh freagraí os ard le',
    speakWithPoneva: 'Guth Poneva',
    screenReader: 'Mo léitheoir scáileáin',
    rate: 'Luas cainte',
    spokenApprovalsLabel: 'Dearbhaigh faomhadh le do ghuth',
    companyVoiceMode: 'Mód gutha',
    companyReadAloud: 'Ceadaigh léamh os ard',
    companySpokenApprovals: 'Ceadaigh faomhadh le guth',
    starting: 'An mód gutha á thosú…',
    finishing: 'Á chríochnú…',
    answering: 'Tá Poneva ag freagairt…',
    speakingInterrupt: 'An freagra á léamh os ard. Brúigh chun cur isteach air.',
    stopReply: 'Stop an léamh os ard',
    reconnecting: 'Ag athnascadh…',
    degraded: 'Aistríodh chuig seirbhís tras-scríofa chúltaca.',
    micOn: 'Micreafón ar siúl',
    pendingLabel: 'Do theachtaireacht, gan seoladh fós',
    pendingAnnouncement:
      'Tá do theachtaireacht réidh le seoladh. Seolann Enter anois í, caitheann Escape uait í.',
    tapToSend: 'Tapáil Seol anois chun í a sheoladh.',
    answerCardFirst: 'Freagair an cárta thuas ar dtús, nó tapáil Seol anois.',
    noSpeechHeard: 'Níor cloiseadh aon chaint.',
    replacedElsewhere: 'Tosaíodh an mód gutha i gcluaisín nó in aip eile.',
    pausedHidden: 'Cuireadh an mód gutha ar sos nuair a folaíodh an leathanach.',
    draftKept: 'Coinníodh do theachtaireacht nár seoladh mar dhréacht.',
    errorPermission:
      'Ceadaigh an micreafón i socruithe an bhrabhsálaí nó an ghléis agus bain triail eile as.',
    errorNoMicrophone: 'Níor aimsíodh aon mhicreafón.',
    errorMicrophoneBusy: 'Tá an micreafón in úsáid ag aip eile.',
    errorMicrophoneLost: 'Dícheanglaíodh an micreafón.',
    errorInsecure: 'Teastaíonn nasc slán (https) ón mód gutha.',
    errorUnsupported: 'Ní féidir leis an mbrabhsálaí seo fuaim a thaifeadadh don mhód gutha.',
    errorRateLimited: 'Tosaíodh an mód gutha ró-mhinic. Bain triail eile as i gceann {seconds} s.',
    errorBudget: 'Tá cuóta an mhóid gutha don lá inniu ídithe.',
    errorOffline: 'Tá tú as líne. Tá an mód gutha ar sos.',
    errorOriginNotAllowed:
      'Ní cheadaítear bunús an leathanaigh seo don mhód gutha. Oscail Chat ón seoladh ceadaithe.',
    errorProviderUnavailable:
      'Níl an tseirbhís urlabhra ar fáil go sealadach. Bain triail eile as gan mhoill.',
    errorSignIn: 'Sínigh isteach chun an mód gutha a úsáid.',
    errorCompanyRequired: 'Tá an mód gutha ar fáil i spás oibre cuideachta.',
    errorGeneric: 'Stop an mód gutha mar gheall ar fhadhb. Bain triail eile as.',
    controlStop: 'Stopadh an léamh os ard.',
    controlRepeat: 'An freagra á léamh arís.',
    controlExit: 'Fágadh an mód gutha.',
    controlRate: 'Socraíodh an luas cainte ar {rate}×.',
    settingsDescription: 'Labhair le Poneva in ionad clóscríobh, agus éist leis na freagraí.',
    enabledLabel: 'Úsáid an mód gutha',
    enabledHint: 'Nuair atá sé seo múchta, míníonn Poneva an mód gutha sula dtosaíonn sé.',
    handsFreeHint: 'Éisteann Poneva agus seolann sé nuair a stopann tú ar feadh tamaillín.',
    undoEndpoint: 'Tar éis duit stopadh ag caint (gan lámha)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Freagair cárta dearbhaithe trí thá nó níl a rá. Teastaíonn tapáil i gcónaí do ghníomhartha i seirbhísí nasctha.',
    saved: 'Sábháilte',
    saveFailed: 'Níorbh fhéidir an t-athrú a shábháil. Bain triail eile as.',
    policyTitle: 'Guth agus léamh os ard',
    policyDescription: 'Baineann na socruithe seo le gach duine i do chuideachta.',
    companyVoiceModeHint:
      'Is féidir le baill an mód gutha a úsáid nuair atá freagraí labhartha ar fáil.',
    companyReadAloudHint: 'Is féidir le Poneva a chuid freagraí a léamh os ard.',
    companySpokenApprovalsHint:
      'Is féidir le baill gníomhartha a dhearbhú lena nguth. Teastaíonn tapáil i gcónaí do ghníomhartha i seirbhísí nasctha.',
  },
  it: {
    voiceMode: 'Modalità vocale',
    enterVoice: 'Parla con Poneva',
    exitVoice: 'Esci dalla modalità vocale',
    listening: 'In ascolto…',
    hearing: 'Ti sento…',
    cancelSpeaking: 'Annulla',
    finishSpeaking: 'Ho finito di parlare',
    sendingIn: 'Invio tra {seconds} s',
    sendNow: 'Invia ora',
    edit: 'Modifica',
    discard: 'Elimina',
    alwaysAsk: 'Chiedi sempre prima di inviare',
    undoWindow: 'Finestra di annullamento',
    settingsPageTitle: 'Voce',
    waitBeforeSending: 'Attesa prima dell’invio',
    paused: 'La modalità vocale è in pausa.',
    resumeVoice: 'Riprendi la voce',
    voiceUnavailable: 'La modalità vocale non è disponibile al momento.',
    errorNotConfigured: 'La modalità vocale non è configurata su questo server.',
    errorPolicyOff: 'La tua azienda ha disattivato la modalità vocale.',
    errorNoEngine: 'Al momento non è disponibile alcun servizio vocale per la modalità vocale.',
    errorTtsUnavailable: 'La lettura ad alta voce non è disponibile al momento.',
    errorPaymentRequired:
      'Il credito di utilizzo della tua azienda è esaurito. Un amministratore può aggiungere una carta; poi riprova.',
    micHeldByVoice: 'Il microfono è in uso dalla modalità vocale.',
    voiceRefusedMeeting: 'La modalità vocale è disattivata mentre si registra una riunione.',
    typeInstead: 'Scrivi invece',
    disclosureTitle: 'Parla con Poneva',
    disclosureBody:
      'Quando la modalità vocale è attiva, ciò che dici viene inviato ai nostri fornitori di riconoscimento vocale ({processors}) e diventa il tuo messaggio.',
    disclosureReadAloud: 'Le risposte lette ad alta voce hanno la voce di {processors}.',
    disclosureApprovals:
      'Il tuo messaggio viene inviato dopo una breve pausa che puoi annullare. Le approvazioni e le modifiche ai tuoi dati aspettano sempre la tua conferma.',
    disclosureExit:
      'Tocca Esci, o chiedi a Poneva di uscire dalla modalità vocale, in qualsiasi momento.',
    turnOn: 'Attiva',
    notNow: 'Non ora',
    degradedTapToSend: 'Passato a un servizio di trascrizione di riserva. Tocca per inviare.',
    idleExited: 'La modalità vocale si è chiusa dopo un po’ di silenzio.',
    settingsTitle: 'Modalità vocale',
    handsFreeLabel: 'Vivavoce',
    readAloudInVoice: 'Leggi le risposte ad alta voce',
    speakWith: 'Leggi le risposte con',
    speakWithPoneva: 'La voce di Poneva',
    screenReader: 'Il mio screen reader',
    rate: 'Velocità di lettura',
    spokenApprovalsLabel: 'Conferma le approvazioni a voce',
    companyVoiceMode: 'Modalità vocale',
    companyReadAloud: 'Consenti la lettura ad alta voce',
    companySpokenApprovals: 'Consenti le approvazioni a voce',
    starting: 'Avvio della modalità vocale…',
    finishing: 'Completamento…',
    answering: 'Poneva sta rispondendo…',
    speakingInterrupt: 'Lettura della risposta. Premi per interrompere.',
    stopReply: 'Interrompi la lettura ad alta voce',
    reconnecting: 'Riconnessione…',
    degraded: 'Passato a un servizio di trascrizione di riserva.',
    micOn: 'Microfono attivo',
    pendingLabel: 'Il tuo messaggio, non ancora inviato',
    pendingAnnouncement:
      'Il tuo messaggio è pronto per l’invio. Invio lo invia ora, Esc lo elimina.',
    tapToSend: 'Tocca Invia ora per inviarlo.',
    answerCardFirst: 'Rispondi prima alla scheda qui sopra, oppure tocca Invia ora.',
    noSpeechHeard: 'Non è stata rilevata alcuna voce.',
    replacedElsewhere: 'La modalità vocale è stata avviata in un’altra scheda o app.',
    pausedHidden: 'La modalità vocale è stata messa in pausa quando la pagina è stata nascosta.',
    draftKept: 'Il tuo messaggio non inviato è stato conservato come bozza.',
    errorPermission:
      'Consenti il microfono nelle impostazioni del browser o del dispositivo, poi riprova.',
    errorNoMicrophone: 'Nessun microfono trovato.',
    errorMicrophoneBusy: 'Il microfono è in uso da un’altra app.',
    errorMicrophoneLost: 'Il microfono è stato scollegato.',
    errorInsecure: 'La modalità vocale richiede una connessione sicura (https).',
    errorUnsupported: 'Questo browser non può registrare audio per la modalità vocale.',
    errorRateLimited: 'La modalità vocale è stata avviata troppe volte. Riprova tra {seconds} s.',
    errorBudget: 'La quota giornaliera della modalità vocale è esaurita.',
    errorOffline: 'Sei offline. La modalità vocale è in pausa.',
    errorOriginNotAllowed:
      'L’origine di questa pagina non è autorizzata per la modalità vocale. Apri Chat dall’indirizzo approvato.',
    errorProviderUnavailable:
      'Il servizio vocale non è temporaneamente disponibile. Riprova tra poco.',
    errorSignIn: 'Accedi per usare la modalità vocale.',
    errorCompanyRequired: 'La modalità vocale è disponibile in uno spazio di lavoro aziendale.',
    errorGeneric: 'La modalità vocale si è interrotta per un problema. Riprova.',
    controlStop: 'Lettura ad alta voce interrotta.',
    controlRepeat: 'Rileggo la risposta.',
    controlExit: 'Uscito dalla modalità vocale.',
    controlRate: 'Velocità di lettura impostata a {rate}×.',
    settingsDescription: 'Parla con Poneva invece di scrivere e ascolta le sue risposte.',
    enabledLabel: 'Usa la modalità vocale',
    enabledHint: 'Quando è disattivata, Poneva spiega la modalità vocale prima che si avvii.',
    handsFreeHint: 'Poneva ascolta e invia quando fai una pausa.',
    undoEndpoint: 'Dopo aver smesso di parlare (vivavoce)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Rispondi a una scheda di conferma dicendo sì o no. Le azioni nei servizi collegati richiedono sempre un tocco.',
    saved: 'Salvato',
    saveFailed: 'Non è stato possibile salvare la modifica. Riprova.',
    policyTitle: 'Voce e lettura ad alta voce',
    policyDescription: 'Queste impostazioni valgono per tutti nella tua azienda.',
    companyVoiceModeHint:
      'I membri possono usare la modalità vocale quando sono disponibili risposte vocali.',
    companyReadAloudHint: 'Poneva può leggere le sue risposte ad alta voce.',
    companySpokenApprovalsHint:
      'I membri possono confermare le azioni a voce. Le azioni nei servizi collegati richiedono sempre un tocco.',
  },
  lv: {
    voiceMode: 'Balss režīms',
    enterVoice: 'Runāt ar Poneva',
    exitVoice: 'Iziet no balss režīma',
    listening: 'Klausās…',
    hearing: 'Dzirdu jūs…',
    cancelSpeaking: 'Atcelt',
    finishSpeaking: 'Gatavs',
    sendingIn: 'Sūtīšana pēc {seconds} s',
    sendNow: 'Sūtīt tūlīt',
    edit: 'Rediģēt',
    discard: 'Atmest',
    alwaysAsk: 'Vienmēr jautāt pirms sūtīšanas',
    undoWindow: 'Atsaukšanas logs',
    settingsPageTitle: 'Balss',
    waitBeforeSending: 'Gaidīšana pirms nosūtīšanas',
    paused: 'Balss režīms ir apturēts.',
    resumeVoice: 'Turpināt ar balsi',
    voiceUnavailable: 'Balss režīms pašlaik nav pieejams.',
    errorNotConfigured: 'Balss režīms šajā serverī nav iestatīts.',
    errorPolicyOff: 'Jūsu uzņēmums ir izslēdzis balss režīmu.',
    errorNoEngine: 'Balss režīmam pašlaik nav pieejams neviens runas pakalpojums.',
    errorTtsUnavailable: 'Lasīšana skaļi pašlaik nav pieejama.',
    errorPaymentRequired:
      'Jūsu uzņēmuma lietojuma kredīts ir izlietots. Administrators var pievienot karti; pēc tam mēģiniet vēlreiz.',
    micHeldByVoice: 'Mikrofonu izmanto balss režīms.',
    voiceRefusedMeeting: 'Balss režīms ir izslēgts, kamēr tiek ierakstīta sanāksme.',
    typeInstead: 'Rakstīt tā vietā',
    disclosureTitle: 'Runāt ar Poneva',
    disclosureBody:
      'Kamēr balss režīms ir ieslēgts, jūsu teiktais tiek nosūtīts mūsu runas pārveides tekstā pakalpojumu sniedzējiem ({processors}) un kļūst par jūsu ziņu.',
    disclosureReadAloud: 'Skaļi nolasītās atbildes ierunā {processors}.',
    disclosureApprovals:
      'Jūsu ziņa tiek nosūtīta pēc īsas pauzes, ko varat atsaukt. Apstiprinājumi un jūsu datu izmaiņas vienmēr gaida jūsu apstiprinājumu.',
    disclosureExit: 'Jebkurā brīdī pieskarieties Iziet vai palūdziet Poneva iziet no balss režīma.',
    turnOn: 'Ieslēgt',
    notNow: 'Ne tagad',
    degradedTapToSend:
      'Pārslēgts uz rezerves transkripcijas pakalpojumu. Pieskarieties, lai nosūtītu.',
    idleExited: 'Balss režīms beidzās pēc klusuma brīža.',
    settingsTitle: 'Balss režīms',
    handsFreeLabel: 'Brīvroku',
    readAloudInVoice: 'Nolasīt atbildes skaļi',
    speakWith: 'Nolasīt atbildes ar',
    speakWithPoneva: 'Poneva balsi',
    screenReader: 'Manu ekrāna lasītāju',
    rate: 'Runas ātrums',
    spokenApprovalsLabel: 'Apstiprināt ar balsi',
    companyVoiceMode: 'Balss režīms',
    companyReadAloud: 'Atļaut skaļu nolasīšanu',
    companySpokenApprovals: 'Atļaut apstiprināšanu ar balsi',
    starting: 'Startē balss režīmu…',
    finishing: 'Pabeidz…',
    answering: 'Poneva atbild…',
    speakingInterrupt: 'Atbilde tiek nolasīta. Nospiediet, lai pārtrauktu.',
    stopReply: 'Apturēt skaļo nolasīšanu',
    reconnecting: 'Atjauno savienojumu…',
    degraded: 'Pārslēgts uz rezerves transkripcijas pakalpojumu.',
    micOn: 'Mikrofons ieslēgts',
    pendingLabel: 'Jūsu ziņa, vēl nav nosūtīta',
    pendingAnnouncement: 'Jūsu ziņa ir gatava sūtīšanai. Enter to nosūta tūlīt, Escape to atmet.',
    tapToSend: 'Pieskarieties Sūtīt tūlīt, lai to nosūtītu.',
    answerCardFirst: 'Vispirms atbildiet uz karti augstāk vai pieskarieties Sūtīt tūlīt.',
    noSpeechHeard: 'Runa netika dzirdēta.',
    replacedElsewhere: 'Balss režīms tika startēts citā cilnē vai lietotnē.',
    pausedHidden: 'Balss režīms tika apturēts, kad lapa tika paslēpta.',
    draftKept: 'Jūsu nenosūtītā ziņa tika saglabāta kā melnraksts.',
    errorPermission: 'Atļaujiet mikrofonu pārlūka vai ierīces iestatījumos un mēģiniet vēlreiz.',
    errorNoMicrophone: 'Mikrofons netika atrasts.',
    errorMicrophoneBusy: 'Mikrofonu izmanto cita lietotne.',
    errorMicrophoneLost: 'Mikrofons tika atvienots.',
    errorInsecure: 'Balss režīmam nepieciešams drošs savienojums (https).',
    errorUnsupported: 'Šī pārlūkprogramma nevar ierakstīt skaņu balss režīmam.',
    errorRateLimited: 'Balss režīms tika startēts pārāk bieži. Mēģiniet vēlreiz pēc {seconds} s.',
    errorBudget: 'Šodienas balss režīma kvota ir izlietota.',
    errorOffline: 'Jūs esat bezsaistē. Balss režīms ir apturēts.',
    errorOriginNotAllowed:
      'Šīs lapas izcelsme nav atļauta balss režīmam. Atveriet Chat no apstiprinātās adreses.',
    errorProviderUnavailable: 'Runas pakalpojums īslaicīgi nav pieejams. Drīzumā mēģiniet vēlreiz.',
    errorSignIn: 'Piesakieties, lai izmantotu balss režīmu.',
    errorCompanyRequired: 'Balss režīms ir pieejams uzņēmuma darbvietā.',
    errorGeneric: 'Balss režīms apstājās problēmas dēļ. Mēģiniet vēlreiz.',
    controlStop: 'Skaļa nolasīšana apturēta.',
    controlRepeat: 'Atbilde tiek nolasīta vēlreiz.',
    controlExit: 'Iziets no balss režīma.',
    controlRate: 'Runas ātrums iestatīts uz {rate}×.',
    settingsDescription: 'Runājiet ar Poneva rakstīšanas vietā un klausieties atbildes.',
    enabledLabel: 'Izmantot balss režīmu',
    enabledHint: 'Kad tas ir izslēgts, Poneva pirms sākšanas izskaidro balss režīmu.',
    handsFreeHint: 'Poneva klausās un nosūta, kad ieturat pauzi.',
    undoEndpoint: 'Pēc runāšanas beigām (brīvroku)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Atbildiet uz apstiprinājuma karti, sakot jā vai nē. Darbībām savienotajos pakalpojumos vienmēr nepieciešams pieskāriens.',
    saved: 'Saglabāts',
    saveFailed: 'Izmaiņas neizdevās saglabāt. Mēģiniet vēlreiz.',
    policyTitle: 'Balss un skaļa nolasīšana',
    policyDescription: 'Šie iestatījumi attiecas uz visiem jūsu uzņēmumā.',
    companyVoiceModeHint:
      'Dalībnieki var izmantot balss režīmu, kad ir pieejamas runātas atbildes.',
    companyReadAloudHint: 'Poneva var skaļi nolasīt savas atbildes.',
    companySpokenApprovalsHint:
      'Dalībnieki var apstiprināt darbības ar balsi. Darbībām savienotajos pakalpojumos vienmēr nepieciešams pieskāriens.',
  },
  lt: {
    voiceMode: 'Balso režimas',
    enterVoice: 'Kalbėti su Poneva',
    exitVoice: 'Išeiti iš balso režimo',
    listening: 'Klausomasi…',
    hearing: 'Girdžiu jus…',
    cancelSpeaking: 'Atšaukti',
    finishSpeaking: 'Baigta',
    sendingIn: 'Bus išsiųsta po {seconds} s',
    sendNow: 'Siųsti dabar',
    edit: 'Redaguoti',
    discard: 'Atmesti',
    alwaysAsk: 'Visada klausti prieš siunčiant',
    undoWindow: 'Anuliavimo langas',
    settingsPageTitle: 'Balsas',
    waitBeforeSending: 'Laukimas prieš siunčiant',
    paused: 'Balso režimas pristabdytas.',
    resumeVoice: 'Tęsti balsu',
    voiceUnavailable: 'Balso režimas šiuo metu nepasiekiamas.',
    errorNotConfigured: 'Balso režimas šiame serveryje nenustatytas.',
    errorPolicyOff: 'Jūsų įmonė išjungė balso režimą.',
    errorNoEngine: 'Balso režimui šiuo metu nepasiekiama jokia kalbos paslauga.',
    errorTtsUnavailable: 'Skaitymas garsiai šiuo metu nepasiekiamas.',
    errorPaymentRequired:
      'Jūsų įmonės naudojimo kreditas išnaudotas. Administratorius gali pridėti kortelę; tada bandykite dar kartą.',
    micHeldByVoice: 'Mikrofoną naudoja balso režimas.',
    voiceRefusedMeeting: 'Balso režimas išjungtas, kol įrašomas susitikimas.',
    typeInstead: 'Rašyti vietoj to',
    disclosureTitle: 'Kalbėti su Poneva',
    disclosureBody:
      'Kai balso režimas įjungtas, tai, ką sakote, siunčiama mūsų kalbos vertimo į tekstą paslaugų teikėjams ({processors}) ir tampa jūsų žinute.',
    disclosureReadAloud: 'Garsiai skaitomus atsakymus įgarsina {processors}.',
    disclosureApprovals:
      'Žinutė išsiunčiama po trumpos pauzės, kurią galite atšaukti. Patvirtinimai ir jūsų duomenų pakeitimai visada laukia jūsų patvirtinimo.',
    disclosureExit: 'Bet kada palieskite Išeiti arba paprašykite Ponevą išeiti iš balso režimo.',
    turnOn: 'Įjungti',
    notNow: 'Ne dabar',
    degradedTapToSend:
      'Perjungta į atsarginę transkripcijos paslaugą. Palieskite, kad išsiųstumėte.',
    idleExited: 'Balso režimas baigėsi po tylos tarpo.',
    settingsTitle: 'Balso režimas',
    handsFreeLabel: 'Laisvų rankų',
    readAloudInVoice: 'Skaityti atsakymus garsiai',
    speakWith: 'Skaityti atsakymus naudojant',
    speakWithPoneva: 'Poneva balsą',
    screenReader: 'Mano ekrano skaitytuvą',
    rate: 'Kalbėjimo greitis',
    spokenApprovalsLabel: 'Patvirtinti balsu',
    companyVoiceMode: 'Balso režimas',
    companyReadAloud: 'Leisti skaityti garsiai',
    companySpokenApprovals: 'Leisti patvirtinti balsu',
    starting: 'Paleidžiamas balso režimas…',
    finishing: 'Baigiama…',
    answering: 'Poneva atsako…',
    speakingInterrupt: 'Atsakymas skaitomas. Paspauskite, kad nutrauktumėte.',
    stopReply: 'Sustabdyti garsinį skaitymą',
    reconnecting: 'Jungiamasi iš naujo…',
    degraded: 'Perjungta į atsarginę transkripcijos paslaugą.',
    micOn: 'Mikrofonas įjungtas',
    pendingLabel: 'Jūsų žinutė, dar neišsiųsta',
    pendingAnnouncement: 'Žinutė paruošta siųsti. Enter ją išsiunčia dabar, Escape ją atmeta.',
    tapToSend: 'Palieskite Siųsti dabar, kad ją išsiųstumėte.',
    answerCardFirst: 'Pirmiausia atsakykite į kortelę aukščiau arba palieskite Siųsti dabar.',
    noSpeechHeard: 'Kalbos negirdėti.',
    replacedElsewhere: 'Balso režimas paleistas kitame skirtuke arba programoje.',
    pausedHidden: 'Balso režimas pristabdytas, kai puslapis buvo paslėptas.',
    draftKept: 'Neišsiųsta žinutė išsaugota kaip juodraštis.',
    errorPermission:
      'Leiskite mikrofoną naršyklės arba įrenginio nustatymuose ir bandykite dar kartą.',
    errorNoMicrophone: 'Mikrofonas nerastas.',
    errorMicrophoneBusy: 'Mikrofoną naudoja kita programa.',
    errorMicrophoneLost: 'Mikrofonas atjungtas.',
    errorInsecure: 'Balso režimui reikia saugaus ryšio (https).',
    errorUnsupported: 'Ši naršyklė negali įrašyti garso balso režimui.',
    errorRateLimited: 'Balso režimas paleistas per dažnai. Bandykite dar kartą po {seconds} s.',
    errorBudget: 'Šiandienos balso režimo kvota išnaudota.',
    errorOffline: 'Esate neprisijungę. Balso režimas pristabdytas.',
    errorOriginNotAllowed:
      'Šio puslapio kilmė neleidžiama balso režimui. Atidarykite Chat iš patvirtinto adreso.',
    errorProviderUnavailable:
      'Kalbos paslauga laikinai nepasiekiama. Netrukus bandykite dar kartą.',
    errorSignIn: 'Prisijunkite, kad naudotumėte balso režimą.',
    errorCompanyRequired: 'Balso režimas pasiekiamas įmonės darbo erdvėje.',
    errorGeneric: 'Balso režimas sustojo dėl problemos. Bandykite dar kartą.',
    controlStop: 'Garsinis skaitymas sustabdytas.',
    controlRepeat: 'Atsakymas skaitomas iš naujo.',
    controlExit: 'Išeita iš balso režimo.',
    controlRate: 'Kalbėjimo greitis nustatytas į {rate}×.',
    settingsDescription: 'Kalbėkite su Poneva, užuot rašę, ir klausykitės atsakymų.',
    enabledLabel: 'Naudoti balso režimą',
    enabledHint: 'Kai tai išjungta, Poneva prieš pradėdama paaiškina balso režimą.',
    handsFreeHint: 'Poneva klausosi ir išsiunčia, kai padarote pauzę.',
    undoEndpoint: 'Baigus kalbėti (laisvų rankų)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Atsakykite į patvirtinimo kortelę sakydami taip arba ne. Veiksmams prijungtose paslaugose visada reikia palietimo.',
    saved: 'Išsaugota',
    saveFailed: 'Pakeitimo išsaugoti nepavyko. Bandykite dar kartą.',
    policyTitle: 'Balsas ir skaitymas garsiai',
    policyDescription: 'Šie nustatymai taikomi visiems jūsų įmonėje.',
    companyVoiceModeHint: 'Nariai gali naudoti balso režimą, kai galimi garsiniai atsakymai.',
    companyReadAloudHint: 'Poneva gali garsiai skaityti savo atsakymus.',
    companySpokenApprovalsHint:
      'Nariai gali patvirtinti veiksmus balsu. Veiksmams prijungtose paslaugose visada reikia palietimo.',
  },
  mt: {
    voiceMode: 'Modalità tal-vuċi',
    enterVoice: 'Kellem lil Poneva',
    exitVoice: 'Oħroġ mill-modalità tal-vuċi',
    listening: 'Qed nisma’…',
    hearing: 'Qed nismgħek…',
    cancelSpeaking: 'Ikkanċella',
    finishSpeaking: 'Lest',
    sendingIn: 'Jintbagħat fi {seconds} s',
    sendNow: 'Ibgħat issa',
    edit: 'Editja',
    discard: 'Armi',
    alwaysAsk: 'Dejjem staqsi qabel tibgħat',
    undoWindow: 'Perjodu biex tneħħi',
    settingsPageTitle: 'Vuċi',
    waitBeforeSending: 'Stenna qabel tibgħat',
    paused: 'Il-modalità tal-vuċi hija sospiża.',
    resumeVoice: 'Kompli bil-vuċi',
    voiceUnavailable: 'Il-modalità tal-vuċi mhijiex disponibbli bħalissa.',
    errorNotConfigured: 'Il-modalità tal-vuċi mhijiex issettjata fuq dan is-server.',
    errorPolicyOff: 'Il-kumpanija tiegħek itfiet il-modalità tal-vuċi.',
    errorNoEngine: 'L-ebda servizz tad-diskors għall-modalità tal-vuċi mhu disponibbli bħalissa.',
    errorTtsUnavailable: 'Il-qari b’leħen għoli mhuwiex disponibbli bħalissa.',
    errorPaymentRequired:
      'Il-kreditu tal-użu tal-kumpanija tiegħek spiċċa. Amministratur jista’ jżid karta; imbagħad erġa’ pprova.',
    micHeldByVoice: 'Il-mikrofonu qed jintuża mill-modalità tal-vuċi.',
    voiceRefusedMeeting: 'Il-modalità tal-vuċi hija mitfija waqt li laqgħa qed tiġi rreġistrata.',
    typeInstead: 'Ikteb minflok',
    disclosureTitle: 'Kellem lil Poneva',
    disclosureBody:
      'Waqt li l-modalità tal-vuċi tkun mixgħula, dak li tgħid jintbagħat lill-fornituri tagħna tad-diskors għal test ({processors}) u jsir il-messaġġ tiegħek.',
    disclosureReadAloud:
      'It-tweġibiet li jinqraw b’leħen għoli jingħataw il-vuċi minn {processors}.',
    disclosureApprovals:
      'Il-messaġġ tiegħek jintbagħat wara pawża qasira li tista’ tħassar. L-approvazzjonijiet u l-bidliet fid-dejta tiegħek dejjem jistennew il-konferma tiegħek.',
    disclosureExit: 'Tektek Oħroġ, jew itlob lil Poneva toħroġ mill-modalità tal-vuċi, meta trid.',
    turnOn: 'Ixgħel',
    notNow: 'Mhux issa',
    degradedTapToSend: 'Inqlibna għal servizz ta’ traskrizzjoni ta’ riżerva. Tektek biex tibgħat.',
    idleExited: 'Il-modalità tal-vuċi ntemmet wara ftit silenzju.',
    settingsTitle: 'Modalità tal-vuċi',
    handsFreeLabel: 'Mingħajr idejn',
    readAloudInVoice: 'Aqra t-tweġibiet b’leħen għoli',
    speakWith: 'Aqra t-tweġibiet bi',
    speakWithPoneva: 'Il-vuċi ta’ Poneva',
    screenReader: 'Il-qarrej tal-iskrin tiegħi',
    rate: 'Veloċità tad-diskors',
    spokenApprovalsLabel: 'Ikkonferma l-approvazzjonijiet bil-vuċi',
    companyVoiceMode: 'Modalità tal-vuċi',
    companyReadAloud: 'Ippermetti l-qari b’leħen għoli',
    companySpokenApprovals: 'Ippermetti approvazzjonijiet bil-vuċi',
    starting: 'Qed tinxtegħel il-modalità tal-vuċi…',
    finishing: 'Qed jitlesta…',
    answering: 'Poneva qed twieġeb…',
    speakingInterrupt: 'Qed tinqara t-tweġiba. Agħfas biex tinterrompi.',
    stopReply: 'Waqqaf il-qari b’leħen għoli',
    reconnecting: 'Qed jerġa’ jgħaqqad…',
    degraded: 'Inqlibna għal servizz ta’ traskrizzjoni ta’ riżerva.',
    micOn: 'Il-mikrofonu mixgħul',
    pendingLabel: 'Il-messaġġ tiegħek, għadu ma ntbagħatx',
    pendingAnnouncement:
      'Il-messaġġ tiegħek lest biex jintbagħat. Enter jibagħtu issa, Escape jarmih.',
    tapToSend: 'Tektek Ibgħat issa biex tibagħtu.',
    answerCardFirst: 'L-ewwel wieġeb il-karta ta’ hawn fuq, jew tektek Ibgħat issa.',
    noSpeechHeard: 'Ma nstema’ l-ebda diskors.',
    replacedElsewhere: 'Il-modalità tal-vuċi bdiet f’tab jew app oħra.',
    pausedHidden: 'Il-modalità tal-vuċi ġiet sospiża meta l-paġna nħbiet.',
    draftKept: 'Il-messaġġ li ma ntbagħatx inżamm bħala abbozz.',
    errorPermission:
      'Ippermetti l-mikrofonu fis-settings tal-browser jew tat-tagħmir u erġa’ pprova.',
    errorNoMicrophone: 'Ma nstab l-ebda mikrofonu.',
    errorMicrophoneBusy: 'Il-mikrofonu qed jintuża minn app oħra.',
    errorMicrophoneLost: 'Il-mikrofonu ġie skonnettjat.',
    errorInsecure: 'Il-modalità tal-vuċi teħtieġ konnessjoni sigura (https).',
    errorUnsupported: 'Dan il-browser ma jistax jirreġistra l-ħoss għall-modalità tal-vuċi.',
    errorRateLimited: 'Il-modalità tal-vuċi nbdiet spiss wisq. Erġa’ pprova fi {seconds} s.',
    errorBudget: 'Il-kwota tal-lum għall-modalità tal-vuċi spiċċat.',
    errorOffline: 'Int offline. Il-modalità tal-vuċi hija sospiża.',
    errorOriginNotAllowed:
      'L-oriġini ta’ din il-paġna mhijiex permessa għall-modalità tal-vuċi. Iftaħ Chat mill-indirizz approvat.',
    errorProviderUnavailable:
      'Is-servizz tad-diskors temporanjament mhuwiex disponibbli. Erġa’ pprova dalwaqt.',
    errorSignIn: 'Idħol biex tuża l-modalità tal-vuċi.',
    errorCompanyRequired:
      'Il-modalità tal-vuċi hija disponibbli fi spazju tax-xogħol tal-kumpanija.',
    errorGeneric: 'Il-modalità tal-vuċi waqfet minħabba problema. Erġa’ pprova.',
    controlStop: 'Il-qari b’leħen għoli waqaf.',
    controlRepeat: 'Qed tinqara t-tweġiba mill-ġdid.',
    controlExit: 'Ħriġt mill-modalità tal-vuċi.',
    controlRate: 'Il-veloċità tad-diskors issettjata għal {rate}×.',
    settingsDescription: 'Kellem lil Poneva minflok tikteb, u isma’ t-tweġibiet.',
    enabledLabel: 'Uża l-modalità tal-vuċi',
    enabledHint: 'Meta dan ikun mitfi, Poneva tispjega l-modalità tal-vuċi qabel tibda.',
    handsFreeHint: 'Poneva tisma’ u tibgħat meta tieqaf ftit.',
    undoEndpoint: 'Wara li tieqaf titkellem (mingħajr idejn)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Wieġeb karta ta’ konferma billi tgħid iva jew le. L-azzjonijiet f’servizzi konnessi dejjem jeħtieġu tektika.',
    saved: 'Issejvjat',
    saveFailed: 'Il-bidla ma setgħetx tiġi ssejvjata. Erġa’ pprova.',
    policyTitle: 'Vuċi u qari b’leħen għoli',
    policyDescription: 'Dawn is-settings japplikaw għal kulħadd fil-kumpanija tiegħek.',
    companyVoiceModeHint:
      'Il-membri jistgħu jużaw il-modalità tal-vuċi meta jkunu disponibbli tweġibiet mitkellma.',
    companyReadAloudHint: 'Poneva tista’ taqra t-tweġibiet tagħha b’leħen għoli.',
    companySpokenApprovalsHint:
      'Il-membri jistgħu jikkonfermaw azzjonijiet bil-vuċi. L-azzjonijiet f’servizzi konnessi dejjem jeħtieġu tektika.',
  },
  pl: {
    voiceMode: 'Tryb głosowy',
    enterVoice: 'Porozmawiaj z Ponevą',
    exitVoice: 'Zakończ tryb głosowy',
    listening: 'Słucham…',
    hearing: 'Słyszę cię…',
    cancelSpeaking: 'Anuluj',
    finishSpeaking: 'Gotowe',
    sendingIn: 'Wysyłanie za {seconds} s',
    sendNow: 'Wyślij teraz',
    edit: 'Edytuj',
    discard: 'Odrzuć',
    alwaysAsk: 'Zawsze pytaj przed wysłaniem',
    undoWindow: 'Czas na cofnięcie',
    settingsPageTitle: 'Głos',
    waitBeforeSending: 'Czas oczekiwania przed wysłaniem',
    paused: 'Tryb głosowy jest wstrzymany.',
    resumeVoice: 'Wznów głos',
    voiceUnavailable: 'Tryb głosowy jest teraz niedostępny.',
    errorNotConfigured: 'Tryb głosowy nie jest skonfigurowany na tym serwerze.',
    errorPolicyOff: 'Twoja firma wyłączyła tryb głosowy.',
    errorNoEngine: 'Żadna usługa mowy dla trybu głosowego nie jest teraz dostępna.',
    errorTtsUnavailable: 'Czytanie na głos nie jest teraz dostępne.',
    errorPaymentRequired:
      'Kredyt użycia Twojej firmy się wyczerpał. Administrator może dodać kartę; potem spróbuj ponownie.',
    micHeldByVoice: 'Mikrofon jest używany przez tryb głosowy.',
    voiceRefusedMeeting: 'Tryb głosowy jest wyłączony podczas nagrywania spotkania.',
    typeInstead: 'Pisz zamiast tego',
    disclosureTitle: 'Porozmawiaj z Ponevą',
    disclosureBody:
      'Gdy tryb głosowy jest włączony, to, co mówisz, trafia do naszych dostawców zamiany mowy na tekst ({processors}) i staje się Twoją wiadomością.',
    disclosureReadAloud: 'Odpowiedzi czytane na głos mają głos {processors}.',
    disclosureApprovals:
      'Wiadomość jest wysyłana po krótkiej przerwie, którą możesz cofnąć. Zatwierdzenia i zmiany Twoich danych zawsze czekają na Twoje potwierdzenie.',
    disclosureExit:
      'W każdej chwili dotknij Zakończ albo poproś Ponevę o wyjście z trybu głosowego.',
    turnOn: 'Włącz',
    notNow: 'Nie teraz',
    degradedTapToSend: 'Przełączono na zapasową usługę transkrypcji. Dotknij, aby wysłać.',
    idleExited: 'Tryb głosowy zakończył się po chwili ciszy.',
    settingsTitle: 'Tryb głosowy',
    handsFreeLabel: 'Bez użycia rąk',
    readAloudInVoice: 'Czytaj odpowiedzi na głos',
    speakWith: 'Czytaj odpowiedzi za pomocą',
    speakWithPoneva: 'Głosu Ponevy',
    screenReader: 'Mojego czytnika ekranu',
    rate: 'Tempo mowy',
    spokenApprovalsLabel: 'Potwierdzaj zatwierdzenia głosem',
    companyVoiceMode: 'Tryb głosowy',
    companyReadAloud: 'Zezwalaj na czytanie na głos',
    companySpokenApprovals: 'Zezwalaj na zatwierdzanie głosem',
    starting: 'Uruchamianie trybu głosowego…',
    finishing: 'Kończenie…',
    answering: 'Poneva odpowiada…',
    speakingInterrupt: 'Czytanie odpowiedzi. Naciśnij, aby przerwać.',
    stopReply: 'Przestań czytać na głos',
    reconnecting: 'Ponowne łączenie…',
    degraded: 'Przełączono na zapasową usługę transkrypcji.',
    micOn: 'Mikrofon włączony',
    pendingLabel: 'Twoja wiadomość, jeszcze niewysłana',
    pendingAnnouncement:
      'Wiadomość jest gotowa do wysłania. Enter wysyła ją teraz, Escape ją odrzuca.',
    tapToSend: 'Dotknij Wyślij teraz, aby ją wysłać.',
    answerCardFirst: 'Najpierw odpowiedz na kartę powyżej albo dotknij Wyślij teraz.',
    noSpeechHeard: 'Nie usłyszano mowy.',
    replacedElsewhere: 'Tryb głosowy uruchomiono w innej karcie lub aplikacji.',
    pausedHidden: 'Tryb głosowy wstrzymano, gdy strona została ukryta.',
    draftKept: 'Niewysłaną wiadomość zachowano jako wersję roboczą.',
    errorPermission:
      'Zezwól na mikrofon w ustawieniach przeglądarki lub urządzenia i spróbuj ponownie.',
    errorNoMicrophone: 'Nie znaleziono mikrofonu.',
    errorMicrophoneBusy: 'Mikrofon jest używany przez inną aplikację.',
    errorMicrophoneLost: 'Mikrofon został odłączony.',
    errorInsecure: 'Tryb głosowy wymaga bezpiecznego połączenia (https).',
    errorUnsupported: 'Ta przeglądarka nie może nagrywać dźwięku w trybie głosowym.',
    errorRateLimited: 'Tryb głosowy uruchamiano zbyt często. Spróbuj ponownie za {seconds} s.',
    errorBudget: 'Dzisiejszy limit trybu głosowego się wyczerpał.',
    errorOffline: 'Jesteś offline. Tryb głosowy jest wstrzymany.',
    errorOriginNotAllowed:
      'Pochodzenie tej strony nie jest dozwolone dla trybu głosowego. Otwórz Chat z zatwierdzonego adresu.',
    errorProviderUnavailable: 'Usługa mowy jest tymczasowo niedostępna. Spróbuj ponownie wkrótce.',
    errorSignIn: 'Zaloguj się, aby używać trybu głosowego.',
    errorCompanyRequired: 'Tryb głosowy jest dostępny w firmowym obszarze roboczym.',
    errorGeneric: 'Tryb głosowy zatrzymał się z powodu problemu. Spróbuj ponownie.',
    controlStop: 'Przestano czytać na głos.',
    controlRepeat: 'Ponowne czytanie odpowiedzi.',
    controlExit: 'Wyjście z trybu głosowego.',
    controlRate: 'Tempo mowy ustawiono na {rate}×.',
    settingsDescription: 'Rozmawiaj z Ponevą zamiast pisać i słuchaj odpowiedzi.',
    enabledLabel: 'Używaj trybu głosowego',
    enabledHint: 'Gdy to jest wyłączone, Poneva wyjaśnia tryb głosowy przed jego uruchomieniem.',
    handsFreeHint: 'Poneva słucha i wysyła, gdy robisz przerwę.',
    undoEndpoint: 'Po zakończeniu mówienia (bez użycia rąk)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Odpowiedz na kartę potwierdzenia, mówiąc tak lub nie. Działania w połączonych usługach zawsze wymagają dotknięcia.',
    saved: 'Zapisano',
    saveFailed: 'Nie udało się zapisać zmiany. Spróbuj ponownie.',
    policyTitle: 'Głos i czytanie na głos',
    policyDescription: 'Te ustawienia dotyczą wszystkich w Twojej firmie.',
    companyVoiceModeHint:
      'Członkowie mogą korzystać z trybu głosowego, gdy dostępne są odpowiedzi głosowe.',
    companyReadAloudHint: 'Poneva może czytać swoje odpowiedzi na głos.',
    companySpokenApprovalsHint:
      'Członkowie mogą potwierdzać działania głosem. Działania w połączonych usługach zawsze wymagają dotknięcia.',
  },
  pt: {
    voiceMode: 'Modo de voz',
    enterVoice: 'Falar com o Poneva',
    exitVoice: 'Sair do modo de voz',
    listening: 'A ouvir…',
    hearing: 'A captar a sua voz…',
    cancelSpeaking: 'Cancelar',
    finishSpeaking: 'Terminei de falar',
    sendingIn: 'A enviar dentro de {seconds} s',
    sendNow: 'Enviar agora',
    edit: 'Editar',
    discard: 'Descartar',
    alwaysAsk: 'Perguntar sempre antes de enviar',
    undoWindow: 'Janela para desfazer',
    settingsPageTitle: 'Voz',
    waitBeforeSending: 'Espera antes de enviar',
    paused: 'O modo de voz está em pausa.',
    resumeVoice: 'Retomar a voz',
    voiceUnavailable: 'O modo de voz não está disponível de momento.',
    errorNotConfigured: 'O modo de voz não está configurado neste servidor.',
    errorPolicyOff: 'A sua empresa desativou o modo de voz.',
    errorNoEngine: 'Não há nenhum serviço de voz disponível para o modo de voz de momento.',
    errorTtsUnavailable: 'A leitura em voz alta não está disponível de momento.',
    errorPaymentRequired:
      'O crédito de utilização da sua empresa esgotou-se. Um administrador pode adicionar um cartão; depois, tente novamente.',
    micHeldByVoice: 'O microfone está a ser usado pelo modo de voz.',
    voiceRefusedMeeting: 'O modo de voz está desligado enquanto uma reunião é gravada.',
    typeInstead: 'Escrever em vez disso',
    disclosureTitle: 'Falar com o Poneva',
    disclosureBody:
      'Enquanto o modo de voz está ligado, o que diz é enviado para os nossos fornecedores de voz para texto ({processors}) e torna-se a sua mensagem.',
    disclosureReadAloud: 'As respostas lidas em voz alta têm a voz da {processors}.',
    disclosureApprovals:
      'A sua mensagem é enviada após uma breve pausa que pode anular. As aprovações e as alterações aos seus dados aguardam sempre a sua confirmação.',
    disclosureExit:
      'Toque em Sair, ou peça ao Poneva para sair do modo de voz, a qualquer momento.',
    turnOn: 'Ligar',
    notNow: 'Agora não',
    degradedTapToSend: 'Mudou para um serviço de transcrição de reserva. Toque para enviar.',
    idleExited: 'O modo de voz terminou após algum tempo em silêncio.',
    settingsTitle: 'Modo de voz',
    handsFreeLabel: 'Mãos-livres',
    readAloudInVoice: 'Ler as respostas em voz alta',
    speakWith: 'Ler as respostas com',
    speakWithPoneva: 'A voz do Poneva',
    screenReader: 'O meu leitor de ecrã',
    rate: 'Velocidade de leitura',
    spokenApprovalsLabel: 'Confirmar aprovações por voz',
    companyVoiceMode: 'Modo de voz',
    companyReadAloud: 'Permitir a leitura em voz alta',
    companySpokenApprovals: 'Permitir aprovações por voz',
    starting: 'A iniciar o modo de voz…',
    finishing: 'A concluir…',
    answering: 'O Poneva está a responder…',
    speakingInterrupt: 'A ler a resposta. Prima para interromper.',
    stopReply: 'Parar a leitura em voz alta',
    reconnecting: 'A restabelecer a ligação…',
    degraded: 'Mudou para um serviço de transcrição de reserva.',
    micOn: 'Microfone ligado',
    pendingLabel: 'A sua mensagem, ainda não enviada',
    pendingAnnouncement:
      'A sua mensagem está pronta a enviar. Enter envia-a agora, Escape descarta-a.',
    tapToSend: 'Toque em Enviar agora para a enviar.',
    answerCardFirst: 'Responda primeiro ao cartão acima, ou toque em Enviar agora.',
    noSpeechHeard: 'Não se ouviu nenhuma fala.',
    replacedElsewhere: 'O modo de voz foi iniciado noutro separador ou aplicação.',
    pausedHidden: 'O modo de voz foi colocado em pausa quando a página ficou oculta.',
    draftKept: 'A sua mensagem não enviada foi guardada como rascunho.',
    errorPermission:
      'Permita o microfone nas definições do navegador ou do dispositivo e tente novamente.',
    errorNoMicrophone: 'Não foi encontrado nenhum microfone.',
    errorMicrophoneBusy: 'O microfone está a ser usado por outra aplicação.',
    errorMicrophoneLost: 'O microfone foi desligado.',
    errorInsecure: 'O modo de voz precisa de uma ligação segura (https).',
    errorUnsupported: 'Este navegador não consegue gravar áudio para o modo de voz.',
    errorRateLimited:
      'O modo de voz foi iniciado demasiadas vezes. Tente novamente dentro de {seconds} s.',
    errorBudget: 'A quota do modo de voz de hoje esgotou-se.',
    errorOffline: 'Está sem ligação. O modo de voz está em pausa.',
    errorOriginNotAllowed:
      'A origem desta página não é permitida para o modo de voz. Abra o Chat pelo endereço aprovado.',
    errorProviderUnavailable:
      'O serviço de voz está temporariamente indisponível. Tente novamente em breve.',
    errorSignIn: 'Inicie sessão para usar o modo de voz.',
    errorCompanyRequired: 'O modo de voz está disponível num espaço de trabalho da empresa.',
    errorGeneric: 'O modo de voz parou devido a um problema. Tente novamente.',
    controlStop: 'A leitura em voz alta parou.',
    controlRepeat: 'A ler a resposta novamente.',
    controlExit: 'Saiu do modo de voz.',
    controlRate: 'Velocidade de leitura definida para {rate}×.',
    settingsDescription: 'Fale com o Poneva em vez de escrever e ouça as respostas.',
    enabledLabel: 'Usar o modo de voz',
    enabledHint: 'Quando está desligado, o Poneva explica o modo de voz antes de começar.',
    handsFreeHint: 'O Poneva ouve e envia quando faz uma pausa.',
    undoEndpoint: 'Depois de parar de falar (mãos-livres)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Responda a um cartão de confirmação dizendo sim ou não. As ações em serviços ligados exigem sempre um toque.',
    saved: 'Guardado',
    saveFailed: 'Não foi possível guardar a alteração. Tente novamente.',
    policyTitle: 'Voz e leitura em voz alta',
    policyDescription: 'Estas definições aplicam-se a todas as pessoas da sua empresa.',
    companyVoiceModeHint:
      'Os membros podem usar o modo de voz quando houver respostas faladas disponíveis.',
    companyReadAloudHint: 'O Poneva pode ler as respostas em voz alta.',
    companySpokenApprovalsHint:
      'Os membros podem confirmar ações por voz. As ações em serviços ligados exigem sempre um toque.',
  },
  ro: {
    voiceMode: 'Mod vocal',
    enterVoice: 'Vorbește cu Poneva',
    exitVoice: 'Ieși din modul vocal',
    listening: 'Se ascultă…',
    hearing: 'Te aud…',
    cancelSpeaking: 'Anulează',
    finishSpeaking: 'Am terminat de vorbit',
    sendingIn: 'Se trimite în {seconds} s',
    sendNow: 'Trimite acum',
    edit: 'Editează',
    discard: 'Renunță',
    alwaysAsk: 'Întreabă mereu înainte de trimitere',
    undoWindow: 'Fereastră de anulare',
    settingsPageTitle: 'Voce',
    waitBeforeSending: 'Așteptare înainte de trimitere',
    paused: 'Modul vocal este pus pe pauză.',
    resumeVoice: 'Reia vocea',
    voiceUnavailable: 'Modul vocal nu este disponibil acum.',
    errorNotConfigured: 'Modul vocal nu este configurat pe acest server.',
    errorPolicyOff: 'Compania ta a dezactivat modul vocal.',
    errorNoEngine: 'Niciun serviciu de vorbire pentru modul vocal nu este disponibil acum.',
    errorTtsUnavailable: 'Citirea cu voce tare nu este disponibilă acum.',
    errorPaymentRequired:
      'Creditul de utilizare al companiei tale s-a epuizat. Un administrator poate adăuga un card; apoi încearcă din nou.',
    micHeldByVoice: 'Microfonul este folosit de modul vocal.',
    voiceRefusedMeeting: 'Modul vocal este oprit cât timp se înregistrează o întâlnire.',
    typeInstead: 'Scrie în schimb',
    disclosureTitle: 'Vorbește cu Poneva',
    disclosureBody:
      'Cât timp modul vocal este activ, ce spui este trimis furnizorilor noștri de transcriere a vorbirii ({processors}) și devine mesajul tău.',
    disclosureReadAloud: 'Răspunsurile citite cu voce tare au vocea {processors}.',
    disclosureApprovals:
      'Mesajul tău este trimis după o scurtă pauză pe care o poți anula. Aprobările și modificările datelor tale așteaptă mereu confirmarea ta.',
    disclosureExit: 'Atinge Ieșire sau cere-i lui Poneva să iasă din modul vocal, oricând.',
    turnOn: 'Activează',
    notNow: 'Nu acum',
    degradedTapToSend:
      'S-a trecut la un serviciu de transcriere de rezervă. Atinge pentru a trimite.',
    idleExited: 'Modul vocal s-a încheiat după o perioadă de liniște.',
    settingsTitle: 'Mod vocal',
    handsFreeLabel: 'Fără mâini',
    readAloudInVoice: 'Citește răspunsurile cu voce tare',
    speakWith: 'Citește răspunsurile cu',
    speakWithPoneva: 'Vocea lui Poneva',
    screenReader: 'Cititorul meu de ecran',
    rate: 'Viteza de citire',
    spokenApprovalsLabel: 'Confirmă aprobările prin voce',
    companyVoiceMode: 'Mod vocal',
    companyReadAloud: 'Permite citirea cu voce tare',
    companySpokenApprovals: 'Permite aprobările prin voce',
    starting: 'Se pornește modul vocal…',
    finishing: 'Se finalizează…',
    answering: 'Poneva răspunde…',
    speakingInterrupt: 'Se citește răspunsul. Apasă pentru a întrerupe.',
    stopReply: 'Oprește citirea cu voce tare',
    reconnecting: 'Se reconectează…',
    degraded: 'S-a trecut la un serviciu de transcriere de rezervă.',
    micOn: 'Microfon pornit',
    pendingLabel: 'Mesajul tău, încă netrimis',
    pendingAnnouncement:
      'Mesajul tău este gata de trimis. Enter îl trimite acum, Escape renunță la el.',
    tapToSend: 'Atinge Trimite acum ca să-l trimiți.',
    answerCardFirst: 'Răspunde mai întâi la cardul de mai sus sau atinge Trimite acum.',
    noSpeechHeard: 'Nu s-a auzit nicio vorbire.',
    replacedElsewhere: 'Modul vocal a pornit în altă filă sau aplicație.',
    pausedHidden: 'Modul vocal a fost pus pe pauză când pagina a fost ascunsă.',
    draftKept: 'Mesajul tău netrimis a fost păstrat ca ciornă.',
    errorPermission:
      'Permite microfonul în setările browserului sau ale dispozitivului, apoi încearcă din nou.',
    errorNoMicrophone: 'Nu s-a găsit niciun microfon.',
    errorMicrophoneBusy: 'Microfonul este folosit de altă aplicație.',
    errorMicrophoneLost: 'Microfonul a fost deconectat.',
    errorInsecure: 'Modul vocal necesită o conexiune securizată (https).',
    errorUnsupported: 'Acest browser nu poate înregistra sunet pentru modul vocal.',
    errorRateLimited: 'Modul vocal a fost pornit prea des. Încearcă din nou în {seconds} s.',
    errorBudget: 'Cota de azi pentru modul vocal s-a epuizat.',
    errorOffline: 'Ești offline. Modul vocal este pus pe pauză.',
    errorOriginNotAllowed:
      'Originea acestei pagini nu este permisă pentru modul vocal. Deschide Chat de la adresa aprobată.',
    errorProviderUnavailable:
      'Serviciul vocal este temporar indisponibil. Încearcă din nou în curând.',
    errorSignIn: 'Conectează-te ca să folosești modul vocal.',
    errorCompanyRequired: 'Modul vocal este disponibil într-un spațiu de lucru al companiei.',
    errorGeneric: 'Modul vocal s-a oprit din cauza unei probleme. Încearcă din nou.',
    controlStop: 'Citirea cu voce tare s-a oprit.',
    controlRepeat: 'Se citește din nou răspunsul.',
    controlExit: 'Ai ieșit din modul vocal.',
    controlRate: 'Viteza de citire este setată la {rate}×.',
    settingsDescription: 'Vorbește cu Poneva în loc să scrii și ascultă răspunsurile.',
    enabledLabel: 'Folosește modul vocal',
    enabledHint: 'Când este oprit, Poneva explică modul vocal înainte să pornească.',
    handsFreeHint: 'Poneva ascultă și trimite când faci o pauză.',
    undoEndpoint: 'După ce te oprești din vorbit (fără mâini)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Răspunde la un card de confirmare spunând da sau nu. Acțiunile din serviciile conectate necesită mereu o atingere.',
    saved: 'Salvat',
    saveFailed: 'Modificarea nu a putut fi salvată. Încearcă din nou.',
    policyTitle: 'Voce și citire cu voce tare',
    policyDescription: 'Aceste setări se aplică tuturor din compania ta.',
    companyVoiceModeHint:
      'Membrii pot folosi modul vocal când răspunsurile vocale sunt disponibile.',
    companyReadAloudHint: 'Poneva își poate citi răspunsurile cu voce tare.',
    companySpokenApprovalsHint:
      'Membrii pot confirma acțiuni prin voce. Acțiunile din serviciile conectate necesită mereu o atingere.',
  },
  sk: {
    voiceMode: 'Hlasový režim',
    enterVoice: 'Hovoriť s Ponevou',
    exitVoice: 'Ukončiť hlasový režim',
    listening: 'Počúvam…',
    hearing: 'Počujem vás…',
    cancelSpeaking: 'Zrušiť',
    finishSpeaking: 'Dohovorené',
    sendingIn: 'Odoslanie o {seconds} s',
    sendNow: 'Odoslať hneď',
    edit: 'Upraviť',
    discard: 'Zahodiť',
    alwaysAsk: 'Pred odoslaním sa vždy opýtať',
    undoWindow: 'Okno na vrátenie',
    settingsPageTitle: 'Hlas',
    waitBeforeSending: 'Čakanie pred odoslaním',
    paused: 'Hlasový režim je pozastavený.',
    resumeVoice: 'Pokračovať hlasom',
    voiceUnavailable: 'Hlasový režim teraz nie je k dispozícii.',
    errorNotConfigured: 'Hlasový režim nie je na tomto serveri nastavený.',
    errorPolicyOff: 'Vaša firma hlasový režim vypla.',
    errorNoEngine: 'Pre hlasový režim teraz nie je k dispozícii žiadna rečová služba.',
    errorTtsUnavailable: 'Čítanie nahlas teraz nie je k dispozícii.',
    errorPaymentRequired:
      'Kredit na používanie vašej firmy sa minul. Správca môže pridať kartu; potom to skúste znova.',
    micHeldByVoice: 'Mikrofón používa hlasový režim.',
    voiceRefusedMeeting: 'Hlasový režim je vypnutý, kým sa nahráva schôdza.',
    typeInstead: 'Radšej písať',
    disclosureTitle: 'Hovoriť s Ponevou',
    disclosureBody:
      'Keď je hlasový režim zapnutý, to, čo poviete, sa posiela našim poskytovateľom prevodu reči na text ({processors}) a stane sa z toho vaša správa.',
    disclosureReadAloud: 'Odpovede čítané nahlas namlúva {processors}.',
    disclosureApprovals:
      'Správa sa odošle po krátkej pauze, ktorú môžete vrátiť. Schválenia a zmeny vašich údajov vždy čakajú na vaše potvrdenie.',
    disclosureExit:
      'Kedykoľvek ťuknite na Ukončiť alebo požiadajte Ponevu, aby hlasový režim ukončila.',
    turnOn: 'Zapnúť',
    notNow: 'Teraz nie',
    degradedTapToSend: 'Prepnuté na záložnú službu prepisu. Ťuknutím odošlete.',
    idleExited: 'Hlasový režim sa skončil po chvíli ticha.',
    settingsTitle: 'Hlasový režim',
    handsFreeLabel: 'Hands-free',
    readAloudInVoice: 'Čítať odpovede nahlas',
    speakWith: 'Čítať odpovede pomocou',
    speakWithPoneva: 'Hlasu Ponevy',
    screenReader: 'Mojej čítačky obrazovky',
    rate: 'Rýchlosť reči',
    spokenApprovalsLabel: 'Potvrdzovať schválenia hlasom',
    companyVoiceMode: 'Hlasový režim',
    companyReadAloud: 'Povoliť čítanie nahlas',
    companySpokenApprovals: 'Povoliť schvaľovanie hlasom',
    starting: 'Spúšťa sa hlasový režim…',
    finishing: 'Dokončuje sa…',
    answering: 'Poneva odpovedá…',
    speakingInterrupt: 'Číta sa odpoveď. Stlačením prerušíte.',
    stopReply: 'Zastaviť čítanie nahlas',
    reconnecting: 'Obnovuje sa pripojenie…',
    degraded: 'Prepnuté na záložnú službu prepisu.',
    micOn: 'Mikrofón zapnutý',
    pendingLabel: 'Vaša správa, zatiaľ neodoslaná',
    pendingAnnouncement:
      'Správa je pripravená na odoslanie. Enter ju odošle hneď, Escape ju zahodí.',
    tapToSend: 'Ťuknutím na Odoslať hneď ju odošlete.',
    answerCardFirst: 'Najprv odpovedzte na kartu vyššie, alebo ťuknite na Odoslať hneď.',
    noSpeechHeard: 'Nebolo počuť žiadnu reč.',
    replacedElsewhere: 'Hlasový režim sa spustil na inej karte alebo v inej aplikácii.',
    pausedHidden: 'Hlasový režim sa pozastavil, keď bola stránka skrytá.',
    draftKept: 'Neodoslaná správa bola uložená ako koncept.',
    errorPermission:
      'Povoľte mikrofón v nastaveniach prehliadača alebo zariadenia a skúste to znova.',
    errorNoMicrophone: 'Nenašiel sa žiadny mikrofón.',
    errorMicrophoneBusy: 'Mikrofón používa iná aplikácia.',
    errorMicrophoneLost: 'Mikrofón bol odpojený.',
    errorInsecure: 'Hlasový režim vyžaduje zabezpečené pripojenie (https).',
    errorUnsupported: 'Tento prehliadač nevie nahrávať zvuk pre hlasový režim.',
    errorRateLimited: 'Hlasový režim bol spustený príliš často. Skúste to znova o {seconds} s.',
    errorBudget: 'Dnešná kvóta hlasového režimu sa minula.',
    errorOffline: 'Ste offline. Hlasový režim je pozastavený.',
    errorOriginNotAllowed:
      'Pôvod tejto stránky nie je povolený pre hlasový režim. Otvorte Chat zo schválenej adresy.',
    errorProviderUnavailable: 'Hlasová služba je dočasne nedostupná. Skúste to znova čoskoro.',
    errorSignIn: 'Na použitie hlasového režimu sa prihláste.',
    errorCompanyRequired: 'Hlasový režim je k dispozícii vo firemnom pracovnom priestore.',
    errorGeneric: 'Hlasový režim sa pre problém zastavil. Skúste to znova.',
    controlStop: 'Čítanie nahlas sa zastavilo.',
    controlRepeat: 'Odpoveď sa číta znova.',
    controlExit: 'Hlasový režim sa ukončil.',
    controlRate: 'Rýchlosť reči je nastavená na {rate}×.',
    settingsDescription: 'Hovorte s Ponevou namiesto písania a počúvajte jej odpovede.',
    enabledLabel: 'Používať hlasový režim',
    enabledHint: 'Keď je to vypnuté, Poneva hlasový režim pred spustením vysvetlí.',
    handsFreeHint: 'Poneva počúva a odošle, keď sa odmlčíte.',
    undoEndpoint: 'Po skončení hovorenia (hands-free)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Odpovedzte na potvrdzovaciu kartu slovom áno alebo nie. Akcie v pripojených službách vždy vyžadujú ťuknutie.',
    saved: 'Uložené',
    saveFailed: 'Zmenu sa nepodarilo uložiť. Skúste to znova.',
    policyTitle: 'Hlas a čítanie nahlas',
    policyDescription: 'Tieto nastavenia platia pre všetkých vo vašej firme.',
    companyVoiceModeHint:
      'Členovia môžu používať hlasový režim, keď sú dostupné hovorené odpovede.',
    companyReadAloudHint: 'Poneva môže svoje odpovede čítať nahlas.',
    companySpokenApprovalsHint:
      'Členovia môžu potvrdzovať akcie hlasom. Akcie v pripojených službách vždy vyžadujú ťuknutie.',
  },
  sl: {
    voiceMode: 'Glasovni način',
    enterVoice: 'Govori s Ponevo',
    exitVoice: 'Zapri glasovni način',
    listening: 'Poslušam…',
    hearing: 'Slišim vas…',
    cancelSpeaking: 'Prekliči',
    finishSpeaking: 'Končano',
    sendingIn: 'Pošiljanje čez {seconds} s',
    sendNow: 'Pošlji zdaj',
    edit: 'Uredi',
    discard: 'Zavrzi',
    alwaysAsk: 'Pred pošiljanjem vedno vprašaj',
    undoWindow: 'Čas za razveljavitev',
    settingsPageTitle: 'Glas',
    waitBeforeSending: 'Čakanje pred pošiljanjem',
    paused: 'Glasovni način je začasno ustavljen.',
    resumeVoice: 'Nadaljuj z glasom',
    voiceUnavailable: 'Glasovni način trenutno ni na voljo.',
    errorNotConfigured: 'Glasovni način na tem strežniku ni nastavljen.',
    errorPolicyOff: 'Vaše podjetje je glasovni način izklopilo.',
    errorNoEngine: 'Za glasovni način trenutno ni na voljo nobena govorna storitev.',
    errorTtsUnavailable: 'Branje na glas trenutno ni na voljo.',
    errorPaymentRequired:
      'Kredit za uporabo vašega podjetja je porabljen. Skrbnik lahko doda kartico, nato poskusite znova.',
    micHeldByVoice: 'Mikrofon uporablja glasovni način.',
    voiceRefusedMeeting: 'Glasovni način je izklopljen, medtem ko se snema sestanek.',
    typeInstead: 'Raje tipkaj',
    disclosureTitle: 'Govori s Ponevo',
    disclosureBody:
      'Ko je glasovni način vklopljen, se to, kar poveste, pošlje našim ponudnikom pretvorbe govora v besedilo ({processors}) in postane vaše sporočilo.',
    disclosureReadAloud: 'Odgovore, prebrane na glas, izgovori {processors}.',
    disclosureApprovals:
      'Sporočilo se pošlje po kratkem premoru, ki ga lahko razveljavite. Odobritve in spremembe vaših podatkov vedno čakajo na vašo potrditev.',
    disclosureExit: 'Kadar koli tapnite Zapri ali prosite Ponevo, naj zapusti glasovni način.',
    turnOn: 'Vklopi',
    notNow: 'Ne zdaj',
    degradedTapToSend: 'Preklopljeno na rezervno storitev prepisa. Tapnite za pošiljanje.',
    idleExited: 'Glasovni način se je končal po daljši tišini.',
    settingsTitle: 'Glasovni način',
    handsFreeLabel: 'Prostoročno',
    readAloudInVoice: 'Beri odgovore na glas',
    speakWith: 'Beri odgovore z',
    speakWithPoneva: 'Ponevinim glasom',
    screenReader: 'Mojim bralnikom zaslona',
    rate: 'Hitrost govora',
    spokenApprovalsLabel: 'Potrjuj odobritve z glasom',
    companyVoiceMode: 'Glasovni način',
    companyReadAloud: 'Dovoli branje na glas',
    companySpokenApprovals: 'Dovoli odobritve z glasom',
    starting: 'Zaganjanje glasovnega načina…',
    finishing: 'Dokončevanje…',
    answering: 'Poneva odgovarja…',
    speakingInterrupt: 'Branje odgovora. Pritisnite za prekinitev.',
    stopReply: 'Ustavi branje na glas',
    reconnecting: 'Ponovno povezovanje…',
    degraded: 'Preklopljeno na rezervno storitev prepisa.',
    micOn: 'Mikrofon je vklopljen',
    pendingLabel: 'Vaše sporočilo, še ni poslano',
    pendingAnnouncement:
      'Sporočilo je pripravljeno za pošiljanje. Enter ga pošlje zdaj, Escape ga zavrže.',
    tapToSend: 'Tapnite Pošlji zdaj, da ga pošljete.',
    answerCardFirst: 'Najprej odgovorite na kartico zgoraj ali tapnite Pošlji zdaj.',
    noSpeechHeard: 'Govora ni bilo slišati.',
    replacedElsewhere: 'Glasovni način se je začel v drugem zavihku ali aplikaciji.',
    pausedHidden: 'Glasovni način se je ustavil, ko je bila stran skrita.',
    draftKept: 'Neposlano sporočilo je shranjeno kot osnutek.',
    errorPermission: 'Dovolite mikrofon v nastavitvah brskalnika ali naprave in poskusite znova.',
    errorNoMicrophone: 'Mikrofona ni bilo mogoče najti.',
    errorMicrophoneBusy: 'Mikrofon uporablja druga aplikacija.',
    errorMicrophoneLost: 'Mikrofon je bil odklopljen.',
    errorInsecure: 'Glasovni način zahteva varno povezavo (https).',
    errorUnsupported: 'Ta brskalnik ne more snemati zvoka za glasovni način.',
    errorRateLimited: 'Glasovni način se je zagnal prevečkrat. Poskusite znova čez {seconds} s.',
    errorBudget: 'Današnja kvota glasovnega načina je porabljena.',
    errorOffline: 'Niste povezani. Glasovni način je začasno ustavljen.',
    errorOriginNotAllowed:
      'Izvor te strani ni dovoljen za glasovni način. Odprite Chat z odobrenega naslova.',
    errorProviderUnavailable: 'Govorna storitev začasno ni na voljo. Kmalu poskusite znova.',
    errorSignIn: 'Za uporabo glasovnega načina se prijavite.',
    errorCompanyRequired: 'Glasovni način je na voljo v delovnem prostoru podjetja.',
    errorGeneric: 'Glasovni način se je zaradi težave ustavil. Poskusite znova.',
    controlStop: 'Branje na glas je ustavljeno.',
    controlRepeat: 'Odgovor se znova bere.',
    controlExit: 'Glasovni način je zaprt.',
    controlRate: 'Hitrost govora je nastavljena na {rate}×.',
    settingsDescription: 'Govorite s Ponevo namesto tipkanja in poslušajte njene odgovore.',
    enabledLabel: 'Uporabljaj glasovni način',
    enabledHint: 'Ko je to izklopljeno, Poneva pred začetkom razloži glasovni način.',
    handsFreeHint: 'Poneva posluša in pošlje, ko naredite premor.',
    undoEndpoint: 'Ko nehate govoriti (prostoročno)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Na potrditveno kartico odgovorite z da ali ne. Dejanja v povezanih storitvah vedno zahtevajo tap.',
    saved: 'Shranjeno',
    saveFailed: 'Spremembe ni bilo mogoče shraniti. Poskusite znova.',
    policyTitle: 'Glas in branje na glas',
    policyDescription: 'Te nastavitve veljajo za vse v vašem podjetju.',
    companyVoiceModeHint:
      'Člani lahko uporabljajo glasovni način, ko so na voljo govorjeni odgovori.',
    companyReadAloudHint: 'Poneva lahko svoje odgovore bere na glas.',
    companySpokenApprovalsHint:
      'Člani lahko dejanja potrjujejo z glasom. Dejanja v povezanih storitvah vedno zahtevajo tap.',
  },
  es: {
    voiceMode: 'Modo de voz',
    enterVoice: 'Habla con Poneva',
    exitVoice: 'Salir del modo de voz',
    listening: 'Escuchando…',
    hearing: 'Te escucho…',
    cancelSpeaking: 'Cancelar',
    finishSpeaking: 'He terminado de hablar',
    sendingIn: 'Se enviará en {seconds} s',
    sendNow: 'Enviar ahora',
    edit: 'Editar',
    discard: 'Descartar',
    alwaysAsk: 'Preguntar siempre antes de enviar',
    undoWindow: 'Ventana para deshacer',
    settingsPageTitle: 'Voz',
    waitBeforeSending: 'Espera antes de enviar',
    paused: 'El modo de voz está en pausa.',
    resumeVoice: 'Reanudar la voz',
    voiceUnavailable: 'El modo de voz no está disponible en este momento.',
    errorNotConfigured: 'El modo de voz no está configurado en este servidor.',
    errorPolicyOff: 'Tu empresa ha desactivado el modo de voz.',
    errorNoEngine: 'No hay ningún servicio de voz disponible para el modo de voz en este momento.',
    errorTtsUnavailable: 'La lectura en voz alta no está disponible ahora.',
    errorPaymentRequired:
      'El crédito de uso de tu empresa se ha agotado. Un administrador puede añadir una tarjeta; después, vuelve a intentarlo.',
    micHeldByVoice: 'El modo de voz está usando el micrófono.',
    voiceRefusedMeeting: 'El modo de voz está desactivado mientras se graba una reunión.',
    typeInstead: 'Escribir en su lugar',
    disclosureTitle: 'Habla con Poneva',
    disclosureBody:
      'Mientras el modo de voz está activado, lo que dices se envía a nuestros proveedores de voz a texto ({processors}) y se convierte en tu mensaje.',
    disclosureReadAloud: 'Las respuestas leídas en voz alta tienen la voz de {processors}.',
    disclosureApprovals:
      'Tu mensaje se envía tras una breve pausa que puedes deshacer. Las aprobaciones y los cambios en tus datos siempre esperan tu confirmación.',
    disclosureExit: 'Toca Salir, o pide a Poneva que salga del modo de voz, en cualquier momento.',
    turnOn: 'Activar',
    notNow: 'Ahora no',
    degradedTapToSend:
      'Se ha cambiado a un servicio de transcripción de respaldo. Toca para enviar.',
    idleExited: 'El modo de voz terminó tras un rato en silencio.',
    settingsTitle: 'Modo de voz',
    handsFreeLabel: 'Manos libres',
    readAloudInVoice: 'Leer las respuestas en voz alta',
    speakWith: 'Leer las respuestas con',
    speakWithPoneva: 'La voz de Poneva',
    screenReader: 'Mi lector de pantalla',
    rate: 'Velocidad de lectura',
    spokenApprovalsLabel: 'Confirmar aprobaciones con la voz',
    companyVoiceMode: 'Modo de voz',
    companyReadAloud: 'Permitir la lectura en voz alta',
    companySpokenApprovals: 'Permitir aprobaciones con la voz',
    starting: 'Iniciando el modo de voz…',
    finishing: 'Terminando…',
    answering: 'Poneva está respondiendo…',
    speakingInterrupt: 'Leyendo la respuesta. Pulsa para interrumpir.',
    stopReply: 'Dejar de leer en voz alta',
    reconnecting: 'Reconectando…',
    degraded: 'Se ha cambiado a un servicio de transcripción de respaldo.',
    micOn: 'Micrófono activado',
    pendingLabel: 'Tu mensaje, aún sin enviar',
    pendingAnnouncement:
      'Tu mensaje está listo para enviarse. Intro lo envía ahora, Escape lo descarta.',
    tapToSend: 'Toca Enviar ahora para enviarlo.',
    answerCardFirst: 'Responde primero a la tarjeta de arriba, o toca Enviar ahora.',
    noSpeechHeard: 'No se oyó ninguna voz.',
    replacedElsewhere: 'El modo de voz se inició en otra pestaña o aplicación.',
    pausedHidden: 'El modo de voz se pausó al ocultarse la página.',
    draftKept: 'Tu mensaje sin enviar se ha guardado como borrador.',
    errorPermission:
      'Permite el micrófono en los ajustes del navegador o del dispositivo e inténtalo de nuevo.',
    errorNoMicrophone: 'No se encontró ningún micrófono.',
    errorMicrophoneBusy: 'Otra aplicación está usando el micrófono.',
    errorMicrophoneLost: 'El micrófono se ha desconectado.',
    errorInsecure: 'El modo de voz necesita una conexión segura (https).',
    errorUnsupported: 'Este navegador no puede grabar audio para el modo de voz.',
    errorRateLimited:
      'El modo de voz se ha iniciado demasiadas veces. Inténtalo de nuevo en {seconds} s.',
    errorBudget: 'Se ha agotado el cupo del modo de voz de hoy.',
    errorOffline: 'Estás sin conexión. El modo de voz está en pausa.',
    errorOriginNotAllowed:
      'El origen de esta página no está permitido para el modo de voz. Abre Chat desde la dirección aprobada.',
    errorProviderUnavailable:
      'El servicio de voz no está disponible temporalmente. Inténtalo de nuevo pronto.',
    errorSignIn: 'Inicia sesión para usar el modo de voz.',
    errorCompanyRequired: 'El modo de voz está disponible en un espacio de trabajo de empresa.',
    errorGeneric: 'El modo de voz se detuvo por un problema. Inténtalo de nuevo.',
    controlStop: 'Se dejó de leer en voz alta.',
    controlRepeat: 'Leyendo de nuevo la respuesta.',
    controlExit: 'Se salió del modo de voz.',
    controlRate: 'Velocidad de lectura ajustada a {rate}×.',
    settingsDescription: 'Habla con Poneva en lugar de escribir y escucha sus respuestas.',
    enabledLabel: 'Usar el modo de voz',
    enabledHint: 'Si está desactivado, Poneva explica el modo de voz antes de que empiece.',
    handsFreeHint: 'Poneva escucha y envía cuando haces una pausa.',
    undoEndpoint: 'Después de dejar de hablar (manos libres)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Responde a una tarjeta de confirmación diciendo sí o no. Las acciones en servicios conectados siempre requieren un toque.',
    saved: 'Guardado',
    saveFailed: 'No se pudo guardar el cambio. Inténtalo de nuevo.',
    policyTitle: 'Voz y lectura en voz alta',
    policyDescription: 'Estos ajustes se aplican a todas las personas de tu empresa.',
    companyVoiceModeHint:
      'Los miembros pueden usar el modo de voz cuando haya respuestas habladas disponibles.',
    companyReadAloudHint: 'Poneva puede leer sus respuestas en voz alta.',
    companySpokenApprovalsHint:
      'Los miembros pueden confirmar acciones con la voz. Las acciones en servicios conectados siempre requieren un toque.',
  },
  sv: {
    voiceMode: 'Röstläge',
    enterVoice: 'Prata med Poneva',
    exitVoice: 'Avsluta röstläget',
    listening: 'Lyssnar…',
    hearing: 'Hör dig…',
    cancelSpeaking: 'Avbryt',
    finishSpeaking: 'Klar',
    sendingIn: 'Skickas om {seconds} s',
    sendNow: 'Skicka nu',
    edit: 'Redigera',
    discard: 'Släng',
    alwaysAsk: 'Fråga alltid innan det skickas',
    undoWindow: 'Ångrafönster',
    settingsPageTitle: 'Röst',
    waitBeforeSending: 'Vänta innan meddelandet skickas',
    paused: 'Röstläget är pausat.',
    resumeVoice: 'Fortsätt med röst',
    voiceUnavailable: 'Röstläget är inte tillgängligt just nu.',
    errorNotConfigured: 'Röstläget är inte konfigurerat på den här servern.',
    errorPolicyOff: 'Ditt företag har stängt av röstläget.',
    errorNoEngine: 'Ingen taltjänst för röstläget är tillgänglig just nu.',
    errorTtsUnavailable: 'Uppläsning är inte tillgänglig just nu.',
    errorPaymentRequired:
      'Företagets användningskredit är slut. En administratör kan lägga till ett kort, försök sedan igen.',
    micHeldByVoice: 'Mikrofonen används av röstläget.',
    voiceRefusedMeeting: 'Röstläget är avstängt medan ett möte spelas in.',
    typeInstead: 'Skriv i stället',
    disclosureTitle: 'Prata med Poneva',
    disclosureBody:
      'När röstläget är på skickas det du säger till våra tjänster för tal till text ({processors}) och blir ditt meddelande.',
    disclosureReadAloud: 'Svar som läses upp får sin röst av {processors}.',
    disclosureApprovals:
      'Ditt meddelande skickas efter en kort paus som du kan ångra. Godkännanden och ändringar av dina data väntar alltid på att du bekräftar dem.',
    disclosureExit: 'Tryck på Avsluta, eller be Poneva lämna röstläget, när du vill.',
    turnOn: 'Slå på',
    notNow: 'Inte nu',
    degradedTapToSend: 'Bytte till en reservtjänst för transkribering. Tryck för att skicka.',
    idleExited: 'Röstläget avslutades efter en stunds tystnad.',
    settingsTitle: 'Röstläge',
    handsFreeLabel: 'Handsfree',
    readAloudInVoice: 'Läs upp svar',
    speakWith: 'Läs upp svar med',
    speakWithPoneva: 'Ponevas röst',
    screenReader: 'Min skärmläsare',
    rate: 'Talhastighet',
    spokenApprovalsLabel: 'Bekräfta godkännanden med rösten',
    companyVoiceMode: 'Röstläge',
    companyReadAloud: 'Tillåt uppläsning',
    companySpokenApprovals: 'Tillåt godkännanden med rösten',
    starting: 'Startar röstläget…',
    finishing: 'Gör klart…',
    answering: 'Poneva svarar…',
    speakingInterrupt: 'Läser upp svaret. Tryck för att avbryta.',
    stopReply: 'Sluta läsa upp',
    reconnecting: 'Återansluter…',
    degraded: 'Bytte till en reservtjänst för transkribering.',
    micOn: 'Mikrofonen är på',
    pendingLabel: 'Ditt meddelande, inte skickat än',
    pendingAnnouncement:
      'Ditt meddelande är klart att skicka. Enter skickar det nu, Escape slänger det.',
    tapToSend: 'Tryck på Skicka nu för att skicka det.',
    answerCardFirst: 'Svara på kortet ovan först, eller tryck på Skicka nu.',
    noSpeechHeard: 'Inget tal hördes.',
    replacedElsewhere: 'Röstläget startades i en annan flik eller app.',
    pausedHidden: 'Röstläget pausades när sidan doldes.',
    draftKept: 'Ditt oskickade meddelande sparades som utkast.',
    errorPermission:
      'Tillåt mikrofonen i webbläsarens eller enhetens inställningar och försök igen.',
    errorNoMicrophone: 'Ingen mikrofon hittades.',
    errorMicrophoneBusy: 'Mikrofonen används av en annan app.',
    errorMicrophoneLost: 'Mikrofonen kopplades från.',
    errorInsecure: 'Röstläget kräver en säker anslutning (https).',
    errorUnsupported: 'Den här webbläsaren kan inte spela in ljud för röstläget.',
    errorRateLimited: 'Röstläget startades för ofta. Försök igen om {seconds} s.',
    errorBudget: 'Dagens kvot för röstläget är slut.',
    errorOffline: 'Du är offline. Röstläget är pausat.',
    errorOriginNotAllowed:
      'Den här sidans ursprung är inte tillåtet för röstläge. Öppna Chat från den godkända adressen.',
    errorProviderUnavailable: 'Taltjänsten är tillfälligt otillgänglig. Försök igen om en stund.',
    errorSignIn: 'Logga in för att använda röstläget.',
    errorCompanyRequired: 'Röstläget finns i en företagsarbetsyta.',
    errorGeneric: 'Röstläget stoppades på grund av ett problem. Försök igen.',
    controlStop: 'Slutade läsa upp.',
    controlRepeat: 'Läser upp svaret igen.',
    controlExit: 'Lämnade röstläget.',
    controlRate: 'Talhastigheten är nu {rate}×.',
    settingsDescription: 'Prata med Poneva i stället för att skriva, och hör svaren.',
    enabledLabel: 'Använd röstläge',
    enabledHint: 'När det här är av förklarar Poneva röstläget innan det startar.',
    handsFreeHint: 'Poneva lyssnar och skickar när du gör en paus.',
    undoEndpoint: 'Efter att du slutat prata (handsfree)',
    undoSeconds: '{seconds} s',
    rateValue: '{rate}×',
    spokenApprovalsHint:
      'Svara på ett bekräftelsekort genom att säga ja eller nej. Åtgärder i anslutna tjänster kräver alltid ett tryck.',
    saved: 'Sparat',
    saveFailed: 'Ändringen kunde inte sparas. Försök igen.',
    policyTitle: 'Röst och uppläsning',
    policyDescription: 'De här inställningarna gäller alla i ditt företag.',
    companyVoiceModeHint: 'Medlemmar kan använda röstläget när talade svar är tillgängliga.',
    companyReadAloudHint: 'Poneva kan läsa upp sina svar.',
    companySpokenApprovalsHint:
      'Medlemmar kan bekräfta åtgärder med rösten. Åtgärder i anslutna tjänster kräver alltid ett tryck.',
  },
};
export default voiceCopy;
